/* Main — entry point, event wiring, WS message handler */

import { state, freshSession, getOrCreateSession, currentSession, setActiveSession, signals, refs, $$, cacheRefs, esc, scrollChat, isNearBottom, shouldApplySeq, noteRunFrame, sessionLastSeq, lockInput, unlockInput, loadPinnedArtifacts } from './state.js';
import { getShell } from './config.js';
import { connectWs, reconnectWs, wsSend, setWsHandler, resumeFrame } from './ws.js';
import { watchdogAction, stallAction } from './watchdog-policy.js';
import { boot, getAuthMode, getActiveConnection, getActiveConversation, setActiveConversation, isAdmin, getUserFeatures, openAddConnection, logout } from './auth.js';
import { initModelPicker, selectedModel } from './model-picker.js';
import { confirm, showApprovalReviewModal, confirmName, initNameConfirmDialog, initUsageDialog, initTemplatePickerDialog } from './dialogs.js';
import { api } from './api.js';
import { bind, effect } from './store.js';
import {
  autonomyAtom, describeAutonomy, refreshAutonomy, applyAutonomyStatus,
  dispatchRun, resetRun, loadToolCatalog, describeTool, toolCatalogAtom, runAtom,
  projectDock, publishAssistantParts, resetAssistantRun,
} from './agent-store.js';
import { initAgentDock } from './agent-dock.js';
import { notify } from './toast.js';
import { buildSelectionPrompt, buildPromotionFixPrompt, buildPromotionFixAllPrompt } from './selection-prompt.js';
import {
  addUserMessage, addAssistantMessage, renderThinking, removeThinking,
  clearChat, loadConversationMessages, getSerializableMessages, restoreMessages,
  getSessionStream, renderStage,
} from './chat.js';
import { chatPanelMarkup } from './chat-markup.js';
import { buildUiContext } from './ui-context.js';
import {
  updateConfiguratorCard, updateConfiguratorCardError,
  finishConfiguratorCard, finishConfiguratorEmbed,
  mountQuoteInPanel, resetQuotePanel,
  cfgFlushQueue,
} from './configurator.js';
import { addTraces, destroyTracePanel, initGlobalTracePanel, rerenderTraces } from './traces.js';
import { renderFileTree, loadFileTree, loadSyncStatuses, checkHealth, highlightActiveFileInTree, filterFileTree, bulkSync, bindArtifactHovers, initSyncLiveBindings } from './file-tree.js';
import { prewarmDatabaseTables } from './database-view.js';
import * as selection from './selection.js';
import { hideDiffPanel, showDiffPanel, animateGrid } from './diff-panel.js';
import { handleAtInput, handleAtKeydown, hideAutocomplete } from './autocomplete.js';
import {
  loadTenants, switchTenant, switchConnection,
  openLoginDialog, submitLogin, setLoginAuthMode, tenantDisplayLabel, tenantHostFromUrl,
  toggleTenantDropdown, closeTenantDropdown, updateTenantCardMeta, updateHostedConnCardMeta,
} from './tenants.js';
import { initSearch, openSearch, reloadChangedTabs, initSidebarSearch, runActiveScriptInWorkbench, validateActiveTab, openOverflowMenuForActiveTab, openFileTab, switchWorkspaceTab } from './search.js';
import { initScaffold } from './scaffold.js';
import { loadPipeline, switchPipelineScope, initFormulaPopup, initExecOrderLiveBinding, renderPipeline } from './pipeline.js';
import { initPreviewPanel, handlePreviewWsMessage, showPreviewPanel } from './template-preview.js';
import { initKnowledgePanel, onKnowledgeActivated } from './knowledge-panel.js';
import { initDbInspector } from './db-inspector.js';
import { populateSyncPanel, renderSyncPanel, focusSyncPanel, updateStatusBarSync } from './sync-panel.js';
import { getEditorContent, isMonacoReady, saveCurrentTab } from './monaco-editor.js';
import { initLayout, toggleZone, collapseZone, expandZone, resetLayout, getLayoutConfig } from './layout-manager.js';
import { openShortcutsDialog } from './shortcuts-dialog.js';
import { initPanels, togglePanel, showPanel, hidePanel, getPanelContainer, getActivePanel, makePanelHeaderDraggable } from './panel-registry.js';
import { initTheme } from './theme.js';
import { openSettingsDialog } from './settings-panel.js';
import { toggleViewMenu } from './view-menu.js';
import {
  ensureActivityShell, setStatus,
  showError,
  renderTurn, finalizeArtifacts, initRenderTurn, initResolveDispatch, initRegenerate,
  renderRoutingChip,
} from './agent-activity.js';
import { lastUserIndex, truncateMessagesAfter } from './chat-edit-helpers.js';
import { completedTurnMessages, pendingTurnMessages } from './chat-persist-helpers.js';
import { isLiveStreamingMsg } from './chat-restore-helpers.js';
import { applyPartEvent, renderPart, updateTextNode } from './agent-parts.js';
import { initTimelinePanel, onTimelineActivated, refreshTimeline, addTimelineEntry } from './timeline.js';
import { refreshAuditView } from './audit-view.js';
import { refreshWorkspaceChanges } from './workspace-changes.js';
import { initTracesPanel, onTracesActivated } from './run-traces.js';
import { initErrorsPanel, onErrorsActivated } from './run-errors.js';
import { initLearningPanel, onLearningActivated } from './run-learning.js';
import { initPipelinesPanel, onPipelinesActivated, refreshPipelines } from './pipelines.js';
import { onPromotionProgress, onPromotionDone } from './promotion-drawer.js';
import { initPromotionPanel, onPromotionActivated } from './promotion.js';
import { onPromotionProgressV2, onPromotionDoneV2 } from './promotion-wizard.js';

import { initRail, renderRail, onAgentEvent, onSessionRunning, onConversationTitled } from './session-rail.js';

// ---------------------------------------------------------------------------
// Stage rendering — chat.js owns the DOM; expose renderStage on window
// so session-rail.js (a sibling <script type="module">) can call it without
// a circular ES module import (session-rail → main → session-rail).
// ---------------------------------------------------------------------------
window.renderStage = renderStage;
// session-rail.js calls this on switch to replay a conversation's loaded messages.
window.loadConversationMessages = loadConversationMessages;

// Hosted: rewrite the active conversation's stored transcript wholesale. ONLY for
// flows that intentionally truncate history (regenerate / edit-and-resend) — the
// routine persists below APPEND instead, so concurrent tabs can't erase each other.
function _persistConversationMessages() {
  if (getAuthMode() !== "hosted") return;
  const cid = getActiveConnection();
  const coid = getActiveConversation();
  if (!cid || !coid) return;
  api.putConnectionMessages(cid, coid, getSerializableMessages(), { replace: true }).catch(() => {});
}

// Hosted: append one turn (the pending question, or the completed question+answer)
// to the server-side transcript. Idempotent by message id — the server-stamped
// assistant id on final — so retries, the server's own safety-net write, and other
// tabs all converge on a single entry instead of clobbering the log.
function _persistTurnAppend(messages) {
  if (getAuthMode() !== "hosted" || !messages.length) return;
  const cid = getActiveConnection();
  const coid = getActiveConversation();
  if (!cid || !coid) return;
  api.appendConnectionTurn(cid, coid, messages).catch(() => {});
}

// E2E test hooks — expose serialize/restore for deterministic round-trip tests.
// Harmless in production (read-only helpers; restore just re-renders the existing
// messages array, same as loadConversationMessages).
window.__test_serializeMessages = getSerializableMessages;
window.__test_restoreMessages   = restoreMessages;
// Allow E2E tests to inject synthetic WS messages (agent_error, etc.) and
// read the active session id for constructing correct conversation_id values.
window.__test_injectWsMessage    = (data) => handleWsMessage(data);
window.__test_getActiveSessionId = ()     => state.activeSessionId;
// Lets a test switch the active per-session-proxy target directly (state.js's
// setActiveSession(), already imported above) the same way a real conversation
// switch does, without needing a real saved conversation:
// loadHistoryConversation()'s only currently-wired
// setActiveSession()+renderStage() pairing is its hosted-mode branch, so this
// is the deterministic seam for exercising the v2 'session-switch' -> Console
// re-render fix (MINOR-5) in local/E2E-stub mode. Caller still fires the real
// 'session-switch' event itself via the (also real, always-on) window.renderStage.
window.__test_setActiveSession   = (convId) => setActiveSession(convId);

// Wire the parts-model renderPart + updateTextNode functions into agent-activity.js.
// This is done once at module-load time to avoid a circular ES-module import
// (agent-parts.js → agent-activity.js → agent-parts.js at the top level).
initRenderTurn(renderPart, updateTextNode);

// C4: wire the resolve dispatcher so permission/question answers are recorded on
// the parts model (persist + non-interactive replay). agent-activity.js can't
// import applyAgentEvent directly (import cycle), so it's injected here. The arrow
// closes over applyAgentEvent (a hoisted function declaration below) and is only
// invoked later when a card is resolved, by which time it is defined.
initResolveDispatch((convId, ev) => applyAgentEvent(convId, ev));

// Wire the Regenerate button (finished-answer action row in agent-activity.js) to
// the run path here. Same injection idiom as initRenderTurn/initResolveDispatch —
// agent-activity.js can't import this module (cycle).
initRegenerate((convId) => regenerateLastAnswer(convId));

// chat.js's inline edit-and-resend button hands the new text back through this
// window hook (it's imported by this module, so it can't import back). Mirrors
// window.renderStage / window.loadConversationMessages.
window.__chatResendEditedMessage = (msgId, text) => editAndResend(msgId, text);

// chat.js's history restore calls back through this hook (same module boundary
// as above) after it carried a live streaming turn across a transcript rebuild:
// the rebuild wiped the turn's bubble, so the live render pipeline — bubble +
// activity shell + keyed parts render, all owned here — must re-mount it for the
// already-streamed activity to stay visible and the live tail to keep rendering.
window.__cpqRehydrateLiveTurn = (convId) => rehydrateLiveTurn(convId);

// ---------------------------------------------------------------------------
// Parts-model helpers
// ---------------------------------------------------------------------------

// Return (or create) the live streaming assistant message for a conversation.
function liveAssistantMsg(convId) {
  const sess = getOrCreateSession(convId);
  let last = sess.messages[sess.messages.length - 1];
  if (!last || last.role !== "assistant" || !last._streaming) {
    last = {
      id: `msg-asst-${Date.now()}`,
      role: "assistant",
      content: "",
      timestamp: new Date().toISOString(),
      _streaming: true,
      parts: [],
    };
    sess.messages.push(last);
  }
  if (!last.parts) last.parts = [];
  return last;
}

// Fold one event into the conversation's live parts and (if active) re-render.
// Also projects the dock view-model from parts (replaces the old dual-dispatch).
function applyAgentEvent(convId, ev) {
  const msg = liveAssistantMsg(convId);
  msg.parts = applyPartEvent(msg.parts, ev);
  if (ev.type === "final") delete msg._streaming;
  // Feed the inline assistant drawer's per-conversation projection.
  // Unconditional (not gated on the active-session guard below) so the
  // drawer stays live even while the user is looking at a different view.
  publishAssistantParts(convId, msg.parts, !!msg._streaming);
  if (convId === state.activeSessionId) {
    const msgEl = ensureAssistantBubble(convId);
    ensureActivityShell(convId, msgEl, cancelInFlight);
    // F2: decide BEFORE the render grows the transcript whether to keep the view
    // pinned to the bottom — only if the user was already at/near it. If they
    // scrolled up to read, leave their position and surface the Jump-to-latest
    // pill instead of yanking them back down.
    const stream = refs.chatStream || document.getElementById('chatStream');
    const stick = stream
      ? isNearBottom(stream.scrollTop, stream.clientHeight, stream.scrollHeight, NEAR_BOTTOM_THRESHOLD_PX)
      : true;
    renderTurn(convId, msg.parts);
    if (stick) scrollChat();
    requestAnimationFrame(updateJumpPill);
    // Feed the dock: project steps+plan from parts (single source of truth).
    // nowLabel: cleared on tool_call (a new tool started); kept on all other events.
    const nowLabel = ev.type === "tool_call" ? "" : (runAtom.peek().nowLabel || "");
    const running = !!msg._streaming;
    runAtom.set({ ...runAtom.peek(), ...projectDock(msg.parts, { running, nowLabel }) });
  }
  return msg;
}

// ---------------------------------------------------------------------------
// Liveness helpers — run watchdog (C2) + stick-to-bottom pill (F2)
// ---------------------------------------------------------------------------

// F2: how close (px) to the bottom still counts as "stuck to bottom" for the
// auto-scroll-follow decision during a streaming run.
const NEAR_BOTTOM_THRESHOLD_PX = 120;

// C2: a run whose socket drops or whose provider hangs may never deliver a
// terminal agent_final/agent_error, stranding the UI in "Thinking…" with a dead
// Cancel. While a run is in-flight we keep a per-conversation timer that any
// agent event re-arms; if it fires we clear the in-flight state and surface a
// recoverable error through the same path agent_error uses.
const RUN_WATCHDOG_MS = 90000;
const RUN_STALLED_MESSAGE = "The run stopped responding — please retry.";
const _runWatchdogs = new Map(); // convId -> setTimeout id (armed while in-flight)

function armRunWatchdog(convId) {
  clearRunWatchdog(convId);
  _runWatchdogs.set(convId, setTimeout(() => _onRunStalled(convId), RUN_WATCHDOG_MS));
}

function clearRunWatchdog(convId) {
  _stallResumed.delete(convId);      // each run gets its own one ask
  const t = _runWatchdogs.get(convId);
  if (t) { clearTimeout(t); _runWatchdogs.delete(convId); }
}

// Conversations that have already spent their one resume attempt on the current run.
const _stallResumed = new Set();

function _onRunStalled(convId) {
  _runWatchdogs.delete(convId); // the timer already fired
  // RESUME FIRST. The 90s window is a guess made on the client, and the server may be
  // perfectly busy behind it -- a slow cold boot outlasts it. Erroring straight away is
  // what discarded the user's turn, because Retry then starts a BRAND NEW one. The socket
  // accepts a resume at any time and answers either with the events we missed (which
  // re-arm this watchdog) or with agent_no_active_run (which stands it down, so the next
  // stall errors for real). Only a stall that survives that ask is a dead run.
  if (convId === state.activeSessionId
      && stallAction({ triedResume: _stallResumed.has(convId) }) === "resume") {
    _stallResumed.add(convId);
    wsSend(resumeFrame(sessionLastSeq(convId)));
    armRunWatchdog(convId);          // give the answer a window of its own
    return;                          // deliberately does NOT tear down the render state
  }
  // No terminal/parking event arrived in the window — recover the UI so it never
  // hangs. Mirror the agent_error handler: clear the in-flight flag and surface a
  // recoverable error with a Retry. The header label is passed explicitly so the
  // settled Process block says how the run ended instead of keeping whatever
  // status the last event left there.
  getOrCreateSession(convId).isThinking = false;
  onAgentEvent(convId, "agent_error");
  if (convId !== state.activeSessionId) return;
  // Tell the server to stop the stalled worker so it stops billing — otherwise the
  // Machine runs until the janitor's hours-later sweep, and retryLast() below starts
  // a FRESH turn that abandons it. MUST stay below the active-conversation guard:
  // the hosted run socket is per-conversation and cancelInFlight() targets whatever
  // conversation that socket belongs to, so cancelling for a background conversation
  // would kill the ACTIVE run instead.
  // (When resume-first lands this path reattaches instead of erroring — WS-B F3.)
  cancelInFlight(convId);
  showError(convId, RUN_STALLED_MESSAGE, retryLast, "Stopped responding");
  dispatchRun({ type: "error" });
}

// Re-arm on any sign of life; clear on terminal/user-blocking events. The
// arm/clear decision is the pure watchdogAction() policy (watchdog-policy.js, unit
// tested) — notably agent_run_started re-arms, so a cold-boot worker that takes
// longer than the 90s window to produce its first real event is not misreported
// as "stopped responding". Centralized so handleWsMessage cases stay untouched.
function _pumpRunWatchdog(data) {
  const convId = data.conversation_id || "default";
  const action = watchdogAction(data.type);
  if (action === "arm") armRunWatchdog(convId);
  else if (action === "clear") clearRunWatchdog(convId);
}

// Pause the watchdogs across a socket gap: while the socket is down NO event can
// arrive, so an armed stall timer would fire a spurious "stopped responding" into
// the very bubble a resume is about to rebuild (reconnect backoff can exceed the
// stall window). Park every armed conversation on disconnect and re-arm on open;
// the resume handshake then corrects the guess — agent_resumed re-arms, and
// agent_no_active_run / the next terminal event stands the timer down (see
// _pumpRunWatchdog). If the run truly died server-side, the re-armed timer still
// fires one stall window after reconnect, so a dead run keeps its recovery path.
const _pausedWatchdogs = new Set();

function _syncWatchdogsToSocket(connected) {
  if (!connected) {
    for (const convId of [..._runWatchdogs.keys()]) {
      _pausedWatchdogs.add(convId);
      clearRunWatchdog(convId);
    }
  } else {
    for (const convId of _pausedWatchdogs) armRunWatchdog(convId);
    _pausedWatchdogs.clear();
  }
}

// F2: "Jump to latest" pill — shown when the user has scrolled up (during or
// after a streaming answer); clicking it scrolls the transcript back to the
// bottom. It lives in .chat-panel (positioned in shell-v2.css) and floats just
// above the composer. A single scroll listener keeps its visibility in sync.
let _jumpPill = null;
let _jumpScrollBound = false;

function ensureJumpPill() {
  if (_jumpPill && _jumpPill.isConnected) return _jumpPill;
  const stream = refs.chatStream || document.getElementById('chatStream');
  const host = stream && stream.parentElement;
  if (!host) return null;
  _jumpPill = document.createElement('button');
  _jumpPill.type = 'button';
  _jumpPill.className = 'jump-to-latest';
  _jumpPill.hidden = true;
  _jumpPill.setAttribute('aria-label', 'Jump to latest');
  _jumpPill.innerHTML = `<i class="ic ic-sm ic-chevron-down"></i><span>Jump to latest</span>`;
  _jumpPill.addEventListener('click', () => { scrollChat(); requestAnimationFrame(updateJumpPill); });
  host.appendChild(_jumpPill);
  if (!_jumpScrollBound) {
    let raf = 0;
    stream.addEventListener('scroll', () => {
      if (raf) return;
      raf = requestAnimationFrame(() => { raf = 0; updateJumpPill(); });
    }, { passive: true });
    _jumpScrollBound = true;
  }
  return _jumpPill;
}

function updateJumpPill() {
  const pill = ensureJumpPill();
  const stream = refs.chatStream || document.getElementById('chatStream');
  if (!pill || !stream) return;
  const near = isNearBottom(stream.scrollTop, stream.clientHeight, stream.scrollHeight, NEAR_BOTTOM_THRESHOLD_PX);
  if (!near) {
    // Float just above the composer; measured so a grown textarea can't hide it.
    // offsetHeight excludes the composer's own bottom margin (shell-v2.css sets
    // one in v2), so read the computed value — CSS stays the single source.
    const composer = pill.parentElement && pill.parentElement.querySelector('.composer');
    const composerMb = composer ? parseFloat(getComputedStyle(composer).marginBottom) || 0 : 0;
    pill.style.bottom = (composer ? composer.offsetHeight + composerMb + 14 : 90) + 'px';
  }
  pill.hidden = near;
}

// ---------------------------------------------------------------------------
// WebSocket message handler
// ---------------------------------------------------------------------------

// Persistent (non-toast) banner for a dead session: ws.js has permanently stopped its
// reconnect loop (the refresh token no longer mints access tokens, or the server said
// forbidden), so nothing recovers on its own — a transient toast would be wrong. The
// button reuses the existing auth flow: auth.js logout() clears the session and reloads
// into the login gate. Inline-styled on purpose (no stylesheet dependency for a state
// where the app is effectively signed out). Idempotent — repeated frames keep one banner.
function showAuthDeadBanner() {
  if (document.getElementById("wsAuthDeadBanner")) return;
  const bar = document.createElement("div");
  bar.id = "wsAuthDeadBanner";
  bar.setAttribute("role", "alert");
  bar.style.cssText = "position:fixed;top:0;left:0;right:0;z-index:100000;display:flex;"
    + "align-items:center;justify-content:center;gap:12px;padding:10px 16px;"
    + "background:#D93025;color:#fff;font:500 13px/1.4 'Inter',system-ui,sans-serif;"
    + "box-shadow:0 2px 8px rgba(0,0,0,.25);";
  const label = document.createElement("span");
  label.textContent = "Session expired - please sign in again";
  const btn = document.createElement("button");
  btn.type = "button";
  btn.textContent = "Sign in";
  btn.style.cssText = "border:0;border-radius:999px;padding:6px 16px;background:#fff;"
    + "color:#D93025;font:600 13px 'Inter',system-ui,sans-serif;cursor:pointer;";
  btn.addEventListener("click", () => { btn.disabled = true; logout(); });
  bar.append(label, btn);
  document.body.appendChild(bar);
}

function handleWsMessage(data) {
  // Resume Phase 0 seq gate: drop a frame the session has already applied (a duplicate or,
  // in Phase 1, a replay overlap). Runs BEFORE the watchdog pump so a dropped duplicate
  // can't re-arm liveness. No-op for unnumbered frames (local /ws/chat carries no seq).
  // The mark is per RUN: a frame from a different run than last tracked reopens the gate
  // first — a tab that did not send the turn must not drop the new run's seq 1.
  if (data.seq != null) {
    const sess = getOrCreateSession(data.conversation_id || "default");
    if (noteRunFrame(sess, data.run_id)) sess.lastSeq = 0;
    if (!shouldApplySeq(sess.lastSeq, data.seq)) return;
    sess.lastSeq = data.seq;
  }
  // C2: keep the stall watchdog in sync with run liveness before dispatching.
  _pumpRunWatchdog(data);
  switch (data.type) {
    case "_ws_status":
      // Socket state changed: pause armed stall watchdogs while it is down, re-arm on open
      // (the resume handshake then confirms or stands them down — see _syncWatchdogsToSocket).
      _syncWatchdogsToSocket(state.wsConnected);
      renderConnStatus();
      break;

    case "_ws_auth_dead":
      // ws.js stopped its reconnect loop for good: the session can't be refreshed (or the
      // server said forbidden). Surface the persistent sign-in banner; the accompanying
      // _ws_status already rendered the socket as disconnected.
      showAuthDeadBanner();
      break;

    case "thinking":
      state.isThinking = true;
      renderThinking();
      break;

    case "chat/restore_ok":
      // Server-side LLM context hydrated from the saved workspace; no UI action.
      break;

    case "response": {
      state.isThinking = false;
      removeThinking();
      handleResponse(data);
      break;
    }

    case "tool_result":
      state.isThinking = false;
      removeThinking();
      addAssistantMessage(`Tool **${data.tool}** ${data.success ? "completed" : "failed"}`, {
        toolResults: data.result ? [{ tool: data.tool, ...data.result }] : undefined,
      });
      addTimelineEntry();
      // Additive v2 dispatch: the v1 timeline panel above is unchanged; the v2
      // Audit view (self-debounced) refreshes live in both local and hosted mode.
      if (getShell() === "v2") refreshAuditView();
      break;

    case "tool_error":
      state.isThinking = false;
      removeThinking();
      addAssistantMessage(`Tool error: ${data.error}`, {});
      break;

    case "configurator_update": {
      const convId = data.conversation_id || "default";
      const sess = state.sessions[convId] || (state.sessions[convId] = freshSession());
      sess.cfgInFlight = false;
      if (convId === state.activeSessionId) {
        if (data.success && data.model) {
          updateConfiguratorCard(data.configuration_id, data.model, data.changed_attribute);
        } else if (!data.success && data.error) {
          updateConfiguratorCardError(data.configuration_id, data.error);
        }
        if (data.traces && data.traces.length > 0) {
          addTraces(data.configuration_id, data.traces);
        }
      } else {
        // not active — buffer traces into sess.cfgTraces but don't touch DOM
        if (data.traces && data.traces.length > 0) {
          (sess.cfgTraces ||= []).push(...data.traces);
        }
      }
      cfgFlushQueue();
      break;
    }

    case "configurator_traces": {
      const convId = data.conversation_id || "default";
      const sess = state.sessions[convId] || (state.sessions[convId] = freshSession());
      if (data.traces && data.traces.length > 0) {
        if (convId === state.activeSessionId) {
          if (sess.cfgConfigId) addTraces(sess.cfgConfigId, data.traces);
        } else {
          (sess.cfgTraces ||= []).push(...data.traces);
        }
      }
      break;
    }

    case "quote_traces": {
      const convId = data.conversation_id || "default";
      const sess = state.sessions[convId] || (state.sessions[convId] = freshSession());
      if (data.traces && data.traces.length > 0 && convId === state.activeSessionId) {
        if (sess.qteQuoteId) addTraces(sess.qteQuoteId, data.traces);
      }
      break;
    }

    case "quote_closed":
      resetQuotePanel();
      break;

    case "configurator_done": {
      const convId = data.conversation_id || "default";
      const sess = state.sessions[convId] || (state.sessions[convId] = freshSession());
      sess.cfgInFlight = false;
      sess.cfgPending = [];
      sess.cfgConfigId = null;
      sess.cfgTraces = [];
      sess.cfgTracePanelOpen = false;
      sess.cfgTraceFilter = "script";
      if (convId === state.activeSessionId) {
        if (data.result?.cancelled) {
          finishConfiguratorCard(data.configuration_id, "cancelled");
          finishConfiguratorEmbed(data.configuration_id, "cancelled");
        } else {
          finishConfiguratorCard(data.configuration_id, "done");
          finishConfiguratorEmbed(data.configuration_id, "done");
        }
        destroyTracePanel(data.configuration_id);
      }
      break;
    }

    case "initial_sync_progress": {
      const pct = data.total > 0 ? Math.round((data.done / data.total) * 100) : 0;
      updateStatusBarSync(data.message || `Syncing... ${pct}%`);
      break;
    }

    case "files_changed": {
      clearTimeout(state._fileChangeTimer);
      const changedPaths = new Set(
        (data.changes || []).map(c => c.path)
      );
      state._fileChangeTimer = setTimeout(() => {
        loadFileTree();
        loadSyncStatuses();
        // Code on disk moved — refresh the live Execution Order view too.
        signals.codebaseRevision.update((n) => n + 1);
      }, 300);
      // Immediately reload any open tab showing a changed file
      if (changedPaths.size > 0) reloadChangedTabs(changedPaths);
      break;
    }

    case "preview_updated":
      handlePreviewWsMessage(data);
      break;

    case "initial_sync_done":
      clearTimeout(state._delayedSyncTimer);
      state._delayedSyncTimer = null;
      if (data.pulled > 0) {
        loadFileTree().then(() => loadSyncStatuses(false));
      } else {
        loadSyncStatuses(false);
      }
      if (data.error) {
        updateStatusBarSync();
        const el = document.getElementById('sbSyncIndicator');
        if (el) {
          el.className = 'sb-sync sb-sync-error';
          const textEl = el.querySelector('.sb-sync-text');
          if (textEl) textEl.textContent = 'Sync failed';
          el.title = `Initial sync failed: ${data.error}`;
        }
      } else {
        updateStatusBarSync();
      }
      updateNavAvatar();
      updateStatusBarGit();
      renderConnStatus();
      refreshAutonomy(); // autonomy is per-tenant — re-resolve after connect/switch
      unlockInput();
      // Sidebar conn-card dot: this is the deferred resolve point for a
      // "new tenant" switch/login (tenants.js's switchTenant()/submitLogin()
      // deliberately leave state.tenantConnecting AND state.tenantSwitching
      // true and defer to this event, same as they already defer
      // unlockInput() above). Both resets are unconditional so this also
      // resolves the boot-time initial-connect case, which goes through
      // loadTenants() directly and never touches either field at all.
      state.tenantConnecting = false;
      state.tenantSwitching = false;
      break;

    case "agent_progress":
      // agent_status / agent_activity supersedes this event; nothing to do here.
      break;

    case "agent_status": {
      const convId = data.conversation_id || "default";
      onAgentEvent(convId, "agent_status");
      if (convId === state.activeSessionId) {
        ensureActivityShell(convId, ensureAssistantBubble(convId), cancelInFlight);
        setStatus(convId, data.label || "Thinking…");
        if (data.route) {
          renderRoutingChip(convId, data.route);
        }
      }
      dispatchRun({ type: "status", label: data.label || "Thinking…" });
      break;
    }
    case "agent_activity": {
      const convId = data.conversation_id || "default";
      // Enrich tool_call with describeTool BEFORE folding into parts.
      let ev;
      if (data.kind === "tool_call" && data.tool !== "TodoWrite") {
        const d = describeTool(data.tool, data.args, toolCatalogAtom.peek());
        ev = { ...data, type: data.kind, verb: d.verb, target: d.target, why: d.why, risk: d.risk };
      } else {
        ev = { ...data, type: data.kind };
      }
      // applyAgentEvent folds into parts AND projects the dock from parts.
      // The old dual-dispatch (dispatchRun tool_call/tool_result/file_write/plan) is gone.
      applyAgentEvent(convId, ev);
      break;
    }
    case "agent_message_delta": {
      const convId = data.conversation_id || "default";
      applyAgentEvent(convId, { type: "message_delta", text: data.text || "" });
      break;
    }
    case "agent_reasoning_delta": {
      // New event (Phase 4); handled uniformly via parts model.
      const convId = data.conversation_id || "default";
      applyAgentEvent(convId, { type: "reasoning_delta", text: data.text || "" });
      break;
    }
    case "agent_message_reset": {
      // Backend closed the current streaming text part (e.g. an in-family
      // escalation restart). Fold a message_reset into the parts model; the
      // render lane's reducer closes the open streaming text part.
      applyAgentEvent(data.conversation_id || "default", { type: "message_reset" });
      break;
    }
    case "agent_final": {
      const convId = data.conversation_id || "default";
      window.__cpqSawFinal = true;   // E2E signal: the current run completed (cleared at each dispatch)
      onAgentEvent(convId, "agent_final");
      const msg = applyAgentEvent(convId, { type: "final", summary: data.summary });
      // Adopt the server-stamped canonical id: every observer of this run (other tabs,
      // the server's safety-net write) keys the turn by it, so persists converge.
      if (data.message_id) msg.id = data.message_id;
      if (convId === state.activeSessionId) finalizeArtifacts(convId, data, pushArtifact, msg.parts);
      addTimelineEntry();
      // Additive v2 dispatch (both modes): hosted's Audit view relies on this
      // agent_final refresh since journal_entry is not forwarded to the browser.
      if (getShell() === "v2") {
        refreshAuditView();
        // The run may have edited workspace files; re-pull the changed-files state so
        // Code/Templates badges are honest the moment the run settles.
        refreshWorkspaceChanges();
      }
      dispatchRun({ type: "final", summary: data.summary });
      // Persist the completed turn as an idempotent append (never the whole document,
      // which would clobber turns another tab wrote in the meantime).
      if (convId === state.activeSessionId) _persistTurnAppend(completedTurnMessages(state.messages, msg));
      if (_promotionFixInFlight) {
        _promotionFixInFlight = false;
        window.dispatchEvent(new CustomEvent("promotion-fix-complete"));
      }
      break;
    }
    case "agent_error": {
      const convId = data.conversation_id || "default";
      // Clear before any early-break so the flag is never left stuck true.
      _promotionFixInFlight = false;
      onAgentEvent(convId, "agent_error");
      if (convId !== state.activeSessionId) break;
      // A cancel is not a failure — retitle the settled Process header accordingly.
      // Prefix-match because the grace-expiry variant opens with the same words.
      const errMsg = data.message || "Agent error";
      const statusLabel = errMsg.startsWith("Run cancelled") ? "Run cancelled" : "Failed";
      showError(convId, errMsg, retryLast, statusLabel);
      dispatchRun({ type: "error" });
      break;
    }
    case "agent_resumed": {
      // Reply to the resume frame sent on socket open: a still-streaming run exists and its
      // missed tail (events with seq in (from_seq, to_seq]) follows, then the live stream.
      // Carries no seq itself, so the gate above always passes it. The watchdog pump already
      // re-armed liveness; the replayed frames rebuild the streaming turn (overlap with
      // already-applied events is dropped by the seq gate). The bracket also announces which
      // run the replay belongs to: a different run than this session last tracked must
      // reopen the seq gate NOW — its frames restart at seq 1 and would otherwise be
      // dropped under the previous run's high-water mark. Surface the handshake so the
      // re-attach isn't invisible: make sure the activity shell exists (after a reload the
      // transcript has no live bubble yet) and show a status label — the replayed/live
      // frames that follow overwrite it naturally.
      const convId = data.conversation_id || "default";
      const sess = getOrCreateSession(convId);
      if (noteRunFrame(sess, data.run_id)) sess.lastSeq = 0;
      if (convId === state.activeSessionId) {
        ensureActivityShell(convId, ensureAssistantBubble(convId), cancelInFlight);
        setStatus(convId, "Reconnected - resuming…");
      }
      break;
    }
    case "conversation_titled": {
      // The server named a still-untitled conversation in the background, seconds
      // after its first reply landed. Patch every surface that shows the name in
      // place — no refetch, and no timer guessing when the write happened.
      const convId = data.conversation_id;
      const title = data.title;
      if (!convId || !title) break;
      onConversationTitled(convId, title);          // v1 session rail
      const conv = (state.savedConversations || []).find((c) => c.id === convId);
      if (conv) {
        conv.title = title;
        renderConversations();
      }
      // v2 Conversations list. Dynamic import because that module statically
      // imports THIS one (a static import back would close the cycle); it is
      // already loaded under the v2 shell, so this resolves without a fetch.
      import("./conversations-view.js")
        .then((m) => m.onConversationTitled?.(convId, title))
        .catch(() => {});
      // v2 chat header — the 4th surface showing this name (chat-header.js).
      import("./chat-header.js")
        .then((m) => m.onConversationTitled?.(convId, title))
        .catch(() => {});
      break;
    }
    case "agent_no_active_run":
      // Reply to the resume frame when there is no run to re-attach to (it finished, or none
      // was streaming). The loaded transcript is already current — completed turns are owned
      // by the server-side conversation store — and the watchdog pump stood the stall timer
      // down, so a reconnect after a run ended can't strand a spurious stall error.
      break;
    case "journal_entry":
      addTimelineEntry(data);
      // Local-only v2 dispatch: hosted's control plane consumes journal events
      // server-side (no browser journal_entry forward), so the hosted Audit view
      // refreshes on agent_final/view-activation instead of here.
      if (getShell() === "v2" && getAuthMode() !== "hosted") refreshAuditView();
      if (getActivePanel('center') === 'pipelines') { refreshPipelines().catch(() => {}); }
      break;
    case "agent_permission_request": {
      const convId = data.conversation_id || "default";
      onAgentEvent(convId, "agent_permission_request");
      applyAgentEvent(convId, { type: "permission_request", request_id: data.request_id, tool: data.tool, input: data.input });
      break;
    }
    case "agent_user_question": {
      const convId = data.conversation_id || "default";
      onAgentEvent(convId, "agent_user_question");
      applyAgentEvent(convId, { type: "user_question", request_id: data.request_id, questions: data.questions });
      break;
    }

    case "error":
      state.isThinking = false;
      removeThinking();
      addAssistantMessage(`Error: ${data.error}`, {});
      break;

    case "promotion_progress":
      onPromotionProgress(data.asset);
      // Additive: the v1 drawer above still gets every event unchanged.
      // The v2 Promotion view's own handler no-ops until it has been
      // mounted at least once (its own _root guard), so this is safe to
      // call even when v2 is active but the user is on another view.
      if (getShell() === "v2") onPromotionProgressV2(data.asset);
      break;
    case "promotion_done":
      onPromotionDone(data.run);
      if (getShell() === "v2") onPromotionDoneV2(data.run);
      break;
  }
}

// ---------------------------------------------------------------------------
// Response handler (async for name confirmation dialog)
// ---------------------------------------------------------------------------

async function handleResponse(data) {
  // Check if any tool result needs name confirmation (new script/calculation)
  let nameConfirmResult = null;
  if (data.tool_results) {
    nameConfirmResult = data.tool_results.find(tr => tr.needs_name_confirm && tr.suggested_name);
  }

  // Show rename dialog if needed — user can accept or change the LLM-suggested name
  if (nameConfirmResult) {
    const isCalc = nameConfirmResult.agent === "calculation_agent";
    const kind = isCalc ? "calculation" : "script";
    const finalName = await confirmName(
      `Name this ${kind}`,
      `The AI suggested a name. You can change it before saving.`,
      nameConfirmResult.suggested_name,
    );

    if (finalName && finalName !== nameConfirmResult.suggested_name) {
      // User renamed — update all references in the response
      const oldName = nameConfirmResult.suggested_name;
      for (const tr of data.tool_results) {
        if (tr.script_name === oldName) tr.script_name = finalName;
        if (tr.suggested_name === oldName) tr.suggested_name = finalName;
      }
      // Update the text message too
      if (data.text) {
        data.text = data.text.replace(new RegExp(`\\*\\*${oldName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\*\\*`, 'g'), `**${finalName}**`);
      }
      // Re-save local file with new name (rename)
      try {
        const code = nameConfirmResult.code;
        if (code) {
          const folder = isCalc ? "custom-calculations" : "global-scripts";
          // Delete old file, write new
          await api.deleteFile(`${folder}/${oldName}.py`).catch(() => {});
          await api.writeFile(`${folder}/${finalName}.py`, code);
        }
      } catch (e) {
        console.warn("Rename local file failed:", e);
      }
    } else if (finalName === null) {
      // User cancelled — still show the message but don't save
      // (code is already auto-saved with original name by the server)
    }
  }

  // Build code blocks and render
  const codeBlocks = [];
  let scriptName = null;

  if (data.tool_results) {
    for (const tr of data.tool_results) {
      // Document template markup (from document agent)
      if (tr.code && tr.agent === "document_agent") {
        codeBlocks.push({
          lang: "xml",
          filename: (tr.script_name || "Template") + ".docx",
          content: tr.code,
          codeDiff: null,
          itemName: tr.script_name || null,
          kind: "document",
        });
        scriptName = tr.script_name || scriptName;
      }
      // Python script code (from script agent or script tools)
      else if (tr.code) {
        const isCalc = tr.agent === "calculation_agent";
        codeBlocks.push({
          lang: "python",
          filename: (tr.script_name || "script") + ".py",
          content: tr.code,
          codeDiff: tr.code_diff || null,
          itemName: tr.script_name || null,
          kind: isCalc ? "calculations" : "scripts",
        });
        scriptName = tr.script_name || scriptName;
      }
      // Calculation script (from field_read_calculation, field_create_calculation, field_update_calculation)
      const calc = tr.calculation || (tr.result && tr.result.verification && tr.result.verification.calculation);
      if (calc && typeof calc === "object" && calc.script) {
        const calcName = calc.name || tr.name || "calculation";
        codeBlocks.push({
          lang: "python",
          filename: calcName + ".py",
          content: calc.script,
          codeDiff: null,
          itemName: calcName,
          kind: "calculations",
        });
        scriptName = calcName;
      }
      // HTML template content (from template_read, etc.)
      if (tr.content && typeof tr.content === "string" && !tr.code) {
        const name = tr.name || "template";
        codeBlocks.push({
          lang: "html",
          filename: name + ".html",
          content: tr.content,
          codeDiff: null,
          itemName: name,
          kind: "templates",
        });
      }
      // JSON properties (from tool results with properties field)
      if (tr.properties && typeof tr.properties === "object" && !tr.code && !tr.content) {
        const name = tr.name || tr.script_name || "properties";
        codeBlocks.push({
          lang: "json",
          filename: `properties/${name}.json`,
          content: JSON.stringify(tr.properties, null, 2),
          codeDiff: null,
          itemName: name,
        });
      }
    }
  }

  addAssistantMessage(data.text || "", {
    toolResults: data.tool_results,
    codeBlocks,
    scriptName,
    needsInput: data.tool_results?.some((tr) => tr.needs_input),
  });

  // Refresh file tree if files were written
  if (nameConfirmResult) {
    loadFileTree();
    loadSyncStatuses();
  }
}

// ---------------------------------------------------------------------------
// Agent activity helpers
// ---------------------------------------------------------------------------

let _lastPrompt = "";
// Set while an autonomous promotion fix-all is in flight so agent_final can
// dispatch promotion-fix-complete back to the drawer.
let _promotionFixInFlight = false;

function ensureAssistantBubble(convId) {
  let bubble = document.querySelector(`.chat-msg[data-conv="${convId}"][data-role="assistant"][data-active="1"]`);
  if (bubble) return bubble;
  bubble = document.createElement("div");
  bubble.className = "chat-msg chat-msg-asst agent-bubble";
  bubble.dataset.conv = convId;
  bubble.dataset.role = "assistant";
  bubble.dataset.active = "1";
  bubble.innerHTML = `<div class="msg-prose"></div>`;
  // Append to this session's container (not #chatStream root) so the streaming
  // bubble lives in the correct per-session div and survives a session switch.
  const log = getSessionStream(convId);
  if (log) {
    // F6: give the per-conversation transcript live-region semantics so screen
    // readers announce assistant text as it streams in. Idempotent — set once.
    if (!log.getAttribute('role')) {
      log.setAttribute('role', 'log');
      log.setAttribute('aria-live', 'polite');
      log.setAttribute('aria-relevant', 'additions text');
      log.setAttribute('aria-label', 'Chat transcript');
    }
    log.appendChild(bubble);
  }
  return bubble;
}

// Re-mount a live streaming turn after a history restore rebuilt its session's
// transcript DOM (see loadConversationMessages's preserveLive; chat.js reaches
// back via window.__cpqRehydrateLiveTurn). The restore preserved the message in
// the session buffer but wiped its bubble — and agent-activity's cached shell
// went stale with it — so rebuild both through the SAME calls the live stream
// uses (ensureActivityShell self-heals a detached shell) and re-render the full
// parts array (renderTurn is a keyed differ over all parts, so the whole turn's
// activity reappears at once). Runs synchronously inside the restore: no frame
// arriving after it can hit a stale shell.
function rehydrateLiveTurn(convId) {
  if (convId !== state.activeSessionId) return;
  const sess = state.sessions[convId];
  const msg = sess && sess.messages && sess.messages[sess.messages.length - 1];
  if (!isLiveStreamingMsg(msg)) return;
  ensureActivityShell(convId, ensureAssistantBubble(convId), cancelInFlight);
  renderTurn(convId, msg.parts || []);
  scrollChat();
}

async function cancelInFlight(convId) {
  // Hosted runs execute in a Fly worker, not the in-process orchestrator, so
  // POST /api/agent/cancel is a no-op for them. Send the cancel frame over the
  // SAME per-connection run socket used to send turns (see sendMessage): the
  // server stops the worker run and emits a terminal agent_error ("Run
  // cancelled.") the UI already treats as run-end. Local/desktop keeps the POST.
  if (getAuthMode() === "hosted") {
    wsSend({ type: "cancel" });
    return;
  }
  await fetch("/api/agent/cancel", {
    method: "POST",
    headers: {"Content-Type": "application/json"},
    body: JSON.stringify({ conversation_id: convId }),
  });
}

async function pushArtifact(convId, artifact) {
  const what = (artifact && artifact.name) || "change";
  // Review this change: fetch the approval manifest (read-only preview) and show
  // a detailed modal before the live-cloud push. On preview failure, falls back
  // to the simple confirm — never blocks the push path.
  const okToPush = await showApprovalReviewModal(artifact, state.tenant);
  if (!okToPush) {
    notify("Push cancelled", "info");
    return null;
  }
  const body = await api.agentPush(convId, artifact);
  console.log("agent push result", body);
  if (body && body.deferred) {
    // FS-dependent tool deferred in hosted mode; surface the server's message.
    // Server shape: {ok: false, deferred: "<code string>", message: "<human text>"}.
    notify(body.message || "Push deferred — apply from the desktop app", "info");
    return body;
  }
  // Report where it landed — trust transparency for a live-cloud push.
  notify(`Pushed ${what} ${body.tenant ? `to ${body.tenant}` : "to cloud"}`, "success");
  return body;
}

// ---------------------------------------------------------------------------
// Backend picker — switch coding-agent backend at runtime
// ---------------------------------------------------------------------------

function shortBackendLabel(spec) {
  // Compact label for the header button (full label lives in the menu).
  if (!spec) return "Backend";
  if (spec === "claude_code") return "Claude Code";
  if (spec === "stub") return "Stub";
  if (spec.startsWith("ollama:")) return "Ollama · " + spec.slice("ollama:".length).split(":")[0];
  if (spec.startsWith("cli:")) {
    const name = spec.slice("cli:".length);
    return name.charAt(0).toUpperCase() + name.slice(1) + " CLI";
  }
  return spec;
}

async function wireBackendPicker() {
  const btn = document.getElementById("btnBackendPicker");
  const menu = document.getElementById("backendMenu");
  const labelEl = document.getElementById("backendPickerLabel");
  if (!btn || !menu || !labelEl) return;

  // Hosted: the coding backend is operator-configured and /api/agent/backends
  // is a gated legacy single-operator route -- hide the control instead of
  // rendering a picker that can never load. style.display (not the hidden
  // attribute) because .backend-picker's display:inline-flex would win.
  if (getAuthMode() === "hosted") {
    btn.style.display = "none";
    return;
  }

  let currentSpec = null;
  let available = [];

  async function refresh() {
    try {
      const r = await fetch("/api/agent/backends");
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      currentSpec = data.current;
      available = data.available || [];
      labelEl.textContent = shortBackendLabel(currentSpec);
      btn.title = `Backend: ${currentSpec}`;
    } catch (e) {
      labelEl.textContent = "Backend";
      console.error("backend list failed", e);
    }
  }

  function renderMenu() {
    menu.innerHTML = "";
    // M11 T1: v2's dense Material menu is NAMES ONLY -- the env-requirement
    // subtitle ("codex CLI on PATH" etc.) is dropped from the DOM entirely
    // for v2, not just visually hidden (an E2E assertion checks the subtitle
    // NODE count is 0, which a CSS display:none could never satisfy, since
    // the node would still exist/match). Gated on the same #appV2-existence
    // check code-view.js already uses elsewhere for v1-vs-v2 branching inside
    // otherwise-shared code -- v1 (no #appV2 in its tree) always takes the
    // `else` branch below, so this function's v1 behavior/output is
    // byte-identical to before. item.requires itself is untouched either way
    // -- this only affects whether THIS render appends a node for it.
    const isV2 = !!document.getElementById("appV2");
    for (const item of available) {
      const row = document.createElement("button");
      row.className = "backend-menu-item" + (item.id === currentSpec ? " active" : "");
      row.type = "button";
      const label = document.createElement("span");
      label.className = "backend-menu-label";
      label.textContent = item.label;
      row.appendChild(label);
      if (!isV2) {
        const hint = document.createElement("span");
        hint.className = "backend-menu-hint";
        hint.textContent = item.requires || "";
        if (hint.textContent) row.appendChild(hint);
      }
      if (item.id === currentSpec) {
        const check = document.createElement("span");
        check.className = "backend-menu-check";
        check.innerHTML = '<i class="ic ic-sm ic-check"></i>';
        row.appendChild(check);
      }
      row.addEventListener("click", async () => {
        if (item.id === currentSpec) { menu.hidden = true; return; }
        menu.hidden = true;
        labelEl.textContent = "Switching…";
        try {
          const r = await fetch("/api/agent/backend", {
            method: "POST",
            headers: {"Content-Type": "application/json"},
            body: JSON.stringify({ spec: item.id, persist: true }),
          });
          if (!r.ok) {
            const text = await r.text().catch(() => "");
            throw new Error(`HTTP ${r.status} ${text}`);
          }
          await refresh();
        } catch (e) {
          console.error("backend switch failed", e);
          labelEl.textContent = shortBackendLabel(currentSpec);
          alert("Failed to switch backend: " + e.message);
        }
      });
      menu.appendChild(row);
    }
  }

  btn.addEventListener("click", async (e) => {
    e.stopPropagation();
    if (!menu.hidden) { menu.hidden = true; return; }
    await refresh();
    renderMenu();
    menu.hidden = false;
  });

  document.addEventListener("click", (e) => {
    if (menu.hidden) return;
    if (e.target.closest(".backend-menu") || e.target.closest("#btnBackendPicker")) return;
    menu.hidden = true;
  });

  // Expose refresh so other code (e.g. post-tenant-init in init()) can update
  // the label once the active tenant's persisted coding_backend is known.
  window.__refreshBackendPicker = refresh;

  await refresh();
}

function retryLast() {
  if (!_lastPrompt) return;
  // Send the last prompt back through the existing chat-send path.
  const input = document.getElementById("promptInput");
  const btn = document.getElementById("btnSend");
  if (!input || !btn) return;
  input.value = _lastPrompt;
  // Trigger updateSendButton by dispatching an input event so the button
  // gets enabled before we click it.
  input.dispatchEvent(new Event("input", { bubbles: true }));
  // Use a microtask delay to let the DOM update before clicking.
  Promise.resolve().then(() => btn.click());
}

// ---------------------------------------------------------------------------
// Connection status
// ---------------------------------------------------------------------------

function renderConnStatus() {
  const connected = state.wsConnected && state.healthOk;

  const dot = refs.connDot;
  if (dot) {
    dot.classList.toggle("connected", state.wsConnected);
    // v2's #connDot has its own tri-state title/aria-label authority now
    // (shell-v2.js's updateConnDot() -- connecting/tenant-based, not raw WS
    // connectivity). Skip this WS-based title write there so the two never
    // race for the same attribute (whichever ran last would otherwise win
    // unpredictably -- e.g. a WS "_ws_status" message landing right after a
    // failed switch resolved could silently clobber an honest "Disconnected"
    // back to "Connected" purely because the socket itself is open). v1 has
    // no other title authority for this dot, so it keeps this write unchanged
    // -- same shell split as the pre-existing `.connected` class toggle above
    // (inert under v2's CSS; this is the title-attribute equivalent of that).
    if (getShell() !== "v2") dot.title = state.wsConnected ? "Connected" : "Disconnected";
  }

  const chip = refs.healthChip;
  if (chip) {
    chip.classList.toggle("ok", connected);
    const label = chip.querySelector(".health-label");
    if (label) label.textContent = connected ? "Connected" : "Connecting...";
  }

  // Also update nav-conn in new layout
  const navConn = refs.navConn;
  if (navConn) {
    const navDot = navConn.querySelector(".health-dot");
    if (navDot) {
      navDot.style.background = connected ? "var(--success)" : "var(--border-strong)";
    }
    navConn.title = state.wsConnected ? "Connected" : "Disconnected";
  }

  // Status bar sync indicator
  updateStatusBarSync();

  // Update tenant selector text — ensure active tenant doesn't show "(offline)"
  if (connected && state.tenant && refs.tenantSelector) {
    const opt = refs.tenantSelector.querySelector(`option[value="${state.tenant}"]`);
    if (opt && opt.textContent.includes('(offline)')) {
      opt.textContent = tenantDisplayLabel(state.tenant);
    }
  }
}

// ---------------------------------------------------------------------------
// Nav avatar
// ---------------------------------------------------------------------------

function updateNavAvatar() {
  const avatar = refs.navAvatar;
  if (avatar && state.tenant) {
    const label = tenantDisplayLabel(state.tenant);
    avatar.textContent = label.slice(0, 2).toUpperCase();
    avatar.title = label;
  }
}

// ---------------------------------------------------------------------------
// Status bar — git info
// ---------------------------------------------------------------------------

async function updateStatusBarGit() {
  if (!state.tenant) {
    if (refs.sbBranch) refs.sbBranch.textContent = '';
    return;
  }
  if (refs.sbBranch) {
    const active = (state.tenants || []).find(t => t.name === state.tenant);
    const url = active?.url || '';
    refs.sbBranch.textContent = tenantHostFromUrl(url);
    refs.sbBranch.title = url ? `SAP CPQ tenant URL — ${url}` : 'SAP CPQ tenant URL';
  }
  try {
    const info = await api.gitStatus();
    if (info && info.git && refs.sbChanges) {
      const n = info.uncommitted_changes || 0;
      refs.sbChanges.textContent = `${n} change${n !== 1 ? 's' : ''}`;
    }
  } catch {
    // Git info is best-effort — ignore errors
  }
}

// ---------------------------------------------------------------------------
// Conversations
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Conversation History
// ---------------------------------------------------------------------------

// Repaint the v2 chat header (chat-header.js) after a conversation-binding
// change that does NOT go through renderStage() — the local-mode switch paths
// below. Hosted paths all end in renderStage(), whose 'session-switch' event
// the header already listens to. Dynamic import: same cycle-avoidance seam as
// the conversation_titled fan-out above; no-op in v1 (module never wired).
function _pingChatHeader() {
  import("./chat-header.js").then((m) => m.updateChatHeaderV2?.()).catch(() => {});
}

async function loadConversations() {
  let fetchOk = true;
  try {
    const data = await api.listConversations();
    state.savedConversations = data.conversations || [];
  } catch (e) {
    state.savedConversations = [];
    fetchOk = false;
  }
  renderConversations();
  // v2 sidebar nav count chip (#v2NavConvCount, shell-v2.js markup): this
  // function is the one chokepoint every conversation-list (re)load already
  // flows through -- the v2 boot path (initV2Shared()) plus the save/
  // activate/reset flows in this file -- so setting the chip here keeps it
  // populated from first paint and fresh after saves made from the Chat
  // view, without a second boot fetch or any new cross-module plumbing.
  // On a FAILED fetch the count is unknown: blank the chip rather than
  // asserting "0" (conversations-view.js's _updateSavedCount(null) makes
  // the same choice for the in-view counters). Direct getElementById, not
  // an import from conversations-view.js, to avoid a static import cycle
  // (that module already imports from this one); null-safe in v1/hosted
  // where the chip markup doesn't exist or this never runs at boot.
  const navChip = document.getElementById("v2NavConvCount");
  if (navChip) navChip.textContent = fetchOk ? String(state.savedConversations.length) : "";
}

function renderConversations() {
  const list = refs.conversationList;
  if (!list) return;
  list.innerHTML = "";

  // Current session card (always first)
  const currentCard = document.createElement("button");
  const msgCount = state.messages.length;
  const isActive = state.activeConversationId === null;
  currentCard.className = `conv-card ${isActive ? "active" : ""}`;
  currentCard.innerHTML = `
    <p class="conv-title">Current session</p>
    <p class="conv-meta">${msgCount} message${msgCount !== 1 ? "s" : ""}</p>
  `;
  currentCard.addEventListener("click", () => switchToCurrentSession());
  list.appendChild(currentCard);

  // Saved conversations
  for (const conv of state.savedConversations) {
    const card = document.createElement("button");
    const active = conv.id === state.activeConversationId;
    card.className = `conv-card ${active ? "active" : ""}`;

    const dateStr = conv.date ? _formatDate(conv.date) : "";
    // "Live" chip: the server says a run is streaming in this conversation right now
    // (hosted list route's active_run). No click behavior \u2014 opening the conversation
    // already reconnects and resumes the run.
    const liveChip = conv.active_run
      ? `<span class="conv-live" title="A run is in progress">Live</span>`
      : "";
    card.innerHTML = `
      <div class="conv-card-header">
        <p class="conv-title">${esc(conv.title || "Untitled")}</p>${liveChip}
        <button class="conv-delete" title="Delete">&times;</button>
      </div>
      <p class="conv-meta">${conv.count} message${conv.count !== 1 ? "s" : ""}${dateStr ? " \u00b7 " + dateStr : ""}</p>
    `;

    // Click card → load conversation
    card.addEventListener("click", (e) => {
      if (e.target.closest(".conv-delete")) return;
      loadHistoryConversation(conv.id);
    });

    // Delete button
    const delBtn = card.querySelector(".conv-delete");
    delBtn?.addEventListener("click", async (e) => {
      e.stopPropagation();
      try {
        await api.deleteConversation(conv.id);
        await loadConversations();
      } catch (err) { /* silent */ }
    });

    list.appendChild(card);
  }
}

function _formatDate(isoStr) {
  try {
    const d = new Date(isoStr);
    const now = new Date();
    const diff = now - d;
    if (diff < 60_000) return "just now";
    if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
    if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
    if (diff < 604_800_000) return `${Math.floor(diff / 86_400_000)}d ago`;
    return d.toLocaleDateString();
  } catch { return ""; }
}

async function saveCurrentConversation() {
  // Hosted has no save-to-history surface: transcripts persist server-side
  // turn by turn, and the local /api/conversations(+/reset) routes this
  // function calls 404 there.
  if (getAuthMode() === "hosted") return;
  const messages = getSerializableMessages();
  if (messages.length === 0) return;
  try {
    await api.saveConversation(messages);
    clearChat();
    await api.resetConversation();
    await loadConversations();
    addAssistantMessage("Conversation saved to history.");
  } catch (e) {
    addAssistantMessage(`Failed to save: ${e.message}`);
  }
}

// Exported (sole additive change to this v1 function, sanctioned for the v2
// sidebar's New-chat CTA, M9 T2 -- mirrors exactly how loadHistoryConversation
// below was exported for the M8 T2 Conversations view): shell-v2.js's
// #v2NewChat click handler imports and calls this SAME function (then
// switches to the chat view) instead of reimplementing the
// save/clear/reset flow. Every existing in-module call site (the chat
// header's own "+" button, wired via bindEvents() below) is unchanged.
export async function startNewConversation() {
  // Hosted: conversations are server-issued per connection and every turn
  // already persists server-side, so the local "save current, then reset"
  // flow below does not apply — its /api/conversations(+/reset) routes exist
  // only in local/desktop mode and 404 in hosted. Create a real server
  // conversation and bind the UI session + run socket to it instead, the
  // same flow as the session rail's "+" button (session-rail.js).
  if (getAuthMode() === "hosted") {
    const cid = getActiveConnection();
    if (!cid) return;
    try {
      const conv = await api.createConnectionConversation(cid, null); // server-issued id
      setActiveConversation(conv.id);   // rebind the run socket target…
      setActiveSession(conv.id);        // …and the active UI session
      reconnectWs();
      state.activeConversationId = conv.id;  // active-row highlight (conversations view)
      await loadConversations();        // refresh the list + v2 nav count chip
      renderRail();                     // the rail picks up the new active session
      renderStage(conv.id);             // fresh empty stage for the new conversation
      (refs.promptInput || document.getElementById("promptInput"))?.focus();
    } catch (e) {
      console.error("create conversation failed", e);
      notify("Could not start a new conversation", "error");
    }
    return;
  }

  const messages = getSerializableMessages();
  // Only the unsaved CURRENT session needs archiving. A conversation loaded
  // from history is already persisted, so re-saving it here would file a
  // near-duplicate copy -- the same guard loadHistoryConversation() applies
  // before it switches away.
  if (messages.length > 0 && state.activeConversationId === null) {
    // A save that fails must not strand the user in the old conversation:
    // report it, then start the new chat anyway (same tolerate-and-continue
    // stance as the resetConversation() call just below).
    try {
      await api.saveConversation(messages);
    } catch {
      notify("Could not save this conversation to history", "error");
    }
  }
  clearChat();
  try { await api.resetConversation(); } catch { /* ok */ }
  state.activeConversationId = null;
  await loadConversations();
  _pingChatHeader();
}

// Exported (sole additive change to this v1 file, sanctioned for the v2
// Conversations view, M8 T2): the v2 list module reuses this exact load flow
// via import rather than reimplementing it, so there is one implementation
// for both the v1 chat-header #historyDropdown and the v2 Conversations
// view. Purely additive -- the keyword only affects cross-module visibility;
// every existing in-module call site (below, and the #historyDropdown click
// handler further down this file) is unchanged.
export async function loadHistoryConversation(convId) {
  // Hosted: server-issued per-connection conversations. Switching mirrors the
  // session rail's flow (session-rail.js row click): bind the UI session and
  // the run socket to the chosen conversation, then replay its persisted
  // messages if the local buffer is empty. No save-before-switch — hosted
  // messages persist server-side turn by turn (appendConnectionTurn).
  if (getAuthMode() === "hosted") {
    const cid = getActiveConnection();
    if (!cid) return;
    if (convId !== getActiveConversation()) {
      setActiveSession(convId);
      setActiveConversation(convId);
      reconnectWs();
      const sess = state.sessions[convId];
      if (!(sess && sess.messages && sess.messages.length)) {
        try {
          const data = await api.connectionMessages(cid, convId);
          if (data && data.messages && data.messages.length) {
            // setActiveSession ran — targets convId. preserveLive: the opened
            // conversation may have a run streaming into it (resumed from
            // another tab/device); don't clobber the live turn.
            loadConversationMessages(data.messages, { preserveLive: true });
          }
        } catch { /* leave empty — the socket still binds to the conversation */ }
      }
      renderStage(convId);
    }
    state.activeConversationId = convId;  // active-row highlight (conversations view)
    return;
  }

  // Save current session first if it has messages
  const currentMessages = getSerializableMessages();
  if (currentMessages.length > 0 && state.activeConversationId === null) {
    // A save that fails must not block the switch the user asked for:
    // report it, then open the requested conversation anyway (same
    // tolerate-and-continue stance as startNewConversation()).
    try {
      await api.saveConversation(currentMessages);
    } catch {
      notify("Could not save this conversation to history", "error");
    }
  }

  try {
    const data = await api.activateConversation(convId);
    state.activeConversationId = convId;
    loadConversationMessages(data.messages || []);
    renderConversations();
    _pingChatHeader();
  } catch (e) {
    addAssistantMessage(`Failed to load conversation: ${e.message}`);
  }
}

function switchToCurrentSession() {
  // "Current unsaved session" is a local-only concept — hosted conversations
  // are all server-issued, and /api/conversations/reset does not exist there.
  if (getAuthMode() === "hosted") return;
  if (state.activeConversationId === null) return; // already on current
  // Just reset to a fresh current session
  clearChat();
  state.activeConversationId = null;
  api.resetConversation().catch(() => {});
  renderConversations();
  _pingChatHeader();
}

// ---------------------------------------------------------------------------
// Chat send
// ---------------------------------------------------------------------------

// The shared "positive hand-off to the agent" idiom: append a prompt block to the
// composer, surface the chat, enable Send, focus — and NEVER auto-send (the user
// reviews first). Every prefill affordance (editor selection, promotion Ask-agent,
// and the hosted read-only hand-offs) funnels through here so the behavior can't drift.
function prefillComposer(block) {
  if (!block) return;
  const input = refs.promptInput || document.getElementById('promptInput');
  if (!input) return;
  input.value = input.value ? `${input.value}\n\n${block}` : block;
  showPanel('chat');            // make sure the chat is visible
  expandZone('right');          // and its zone open
  input.dispatchEvent(new Event('input', { bubbles: true })); // auto-resize + enable Send
  input.focus();
}

// Prefill the composer from an editor selection (the Explain, Fix, and Send actions).
function prefillComposerFromSelection(detail) {
  if (!detail || !detail.ctx) return;
  prefillComposer(buildSelectionPrompt(detail.verb, detail.ctx));
}

// Prefill the composer from a failed/blocked promotion row ("Ask agent" button).
function prefillComposerFromPromotionFailure(detail) {
  if (!detail) return;
  prefillComposer(buildPromotionFixPrompt(detail));
}

// Auto-send a consolidated fix request for ALL failed/blocked promotion assets.
// Called from the "Fix N with agent" footer button via the promotion-fix-agent event.
// REPLACE semantics — this is a self-contained request, not an append.
function fixWithAgent(detail) {
  if (!detail) return;
  // Guard: if agent is already running, don't set the flag (sendMessage will silently
  // return and we'd leave _promotionFixInFlight stuck true forever).
  if (state.inputLocked) {
    notify("Agent is busy — wait for it to finish before fixing", "info");
    return;
  }
  const input = refs.promptInput || document.getElementById('promptInput');
  if (!input) return;
  const prompt = buildPromotionFixAllPrompt(detail);
  input.value = prompt;
  showPanel('chat');
  expandZone('right');
  input.dispatchEvent(new Event('input', { bubbles: true }));
  _promotionFixInFlight = true;
  sendMessage();
}

function sendMessage() {
  if (state.inputLocked) return;
  const input = refs.promptInput || document.getElementById('promptInput');
  if (!input) return;
  const text = input.value.trim();
  const atts = state.pendingAttachments || [];
  if (!text && atts.length === 0) return;

  // Fresh run — clear the dock's previous plan/timeline.
  resetRun();

  // Build the full prompt: image refs first (one line each), markdown/text
  // blocks with inlined content, then optional DOCX block, then the user's text.
  // Image paths are absolute so the agent's Read tool can open them.
  const images = atts.filter(a => a.kind === "image");
  const texts = atts.filter(a => a.kind === "text");
  const docx = atts.find(a => a.kind === "docx") || null;

  const parts = [];
  for (const img of images) {
    parts.push(`[Attached image: ${img.path}]`);
  }
  for (const t of texts) {
    const fence = t.format === "markdown" ? "markdown" : "";
    parts.push(
      `[Attached ${t.format || "text"}: ${t.filename}]\n` +
      // Hosted uploads are extraction-only (no server path) — the content below is the
      // whole attachment; local keeps the saved-path line unchanged.
      (t.path ? `Saved to: ${t.path}\n` : "") +
      `Content:\n\`\`\`${fence}\n${t.content || ""}\n\`\`\``
    );
  }
  if (docx) {
    const meta = docx.structure || {};
    parts.push(
      `[Attached DOCX template: ${docx.filename}]\n` +
      (docx.saved_to ? `Original file saved to: ${docx.saved_to}\n` : "") +
      `Structure: ${meta.tables || 0} table(s), ${meta.sections || 1} section(s), ${docx.tag_count} tag(s).\n` +
      `Template content (with table structure preserved):\n\`\`\`\n${docx.text}\n\`\`\``
    );
  }

  let fullMessage;
  if (parts.length > 0) {
    let fallbackText;
    if (docx) fallbackText = "Please analyze this template and suggest improvements.";
    else if (texts.length > 0 && images.length === 0) fallbackText = `Please review the attached ${texts.length > 1 ? "files" : "file"}.`;
    else fallbackText = "Please look at the attached image" + (images.length > 1 ? "s." : ".");
    fullMessage = parts.join("\n") + "\n\n" + (text || fallbackText);
  } else {
    fullMessage = text;
  }

  // User-visible message in the chat stream — show the typed text, or a
  // short note if they sent only attachments
  let displayText = text;
  if (!displayText) {
    const labels = [];
    if (images.length === 1) labels.push("1 image");
    else if (images.length > 1) labels.push(`${images.length} images`);
    if (texts.length === 1) labels.push("1 markdown");
    else if (texts.length > 1) labels.push(`${texts.length} markdown files`);
    if (docx) labels.push("DOCX template");
    displayText = `(attached ${labels.join(" + ")})`;
  }
  addUserMessage(displayText);
  // Stash the full backend prompt on the user message so regenerate / edit-and-
  // resend can re-run the EXACT prompt (incl. any attachment context) instead of
  // the display text. Serialized alongside the message when it differs from the
  // display text, so the server-side safety net can recognize its own copy of
  // the question and regenerate keeps the exact prompt across reloads.
  const sentMsg = state.messages[state.messages.length - 1];
  if (sentMsg) sentMsg.prompt = fullMessage;
  if (atts.length > 0) clearAttachments();

  _dispatchTurn(fullMessage);

  input.value = "";
  autoResize(input);
  updateSendButton();
}

// Dispatch a prepared prompt to the backend and stand up the in-flight UI
// (persist, thinking indicator, activity shell, run watchdog). Shared by the
// composer send path (sendMessage) and the regenerate / edit-and-resend paths.
// The caller owns the user bubble: sendMessage adds it, regenerate keeps it,
// edit-and-resend rewrites it. resetRun() is the caller's responsibility too.
function _dispatchTurn(fullMessage) {
  _persistTurnAppend(pendingTurnMessages(state.messages));  // persist the just-asked question (idempotent append)
  const payload = { type: "chat", message: fullMessage, conversation_id: state.activeSessionId };
  if (state.cfgConfigId && state.cfgTraces.length > 0) {
    payload.trace_context = {
      action_ids: state.cfgTraces.map(a => a.action_id),
    };
  } else if (state.cfgConfigId) {
    // Configurator open but traces cleared — explicitly empty
    payload.trace_context = { action_ids: [] };
  }

  // Attach active editor content so the agent knows what the user is looking at
  if (state.activeWorkspaceTab && isMonacoReady()) {
    const activeTab = state.workspaceTabs.find(t => t.id === state.activeWorkspaceTab);
    if (activeTab) {
      const content = getEditorContent();
      if (content != null) {
        payload.editor_context = {
          path: activeTab.path,
          name: activeTab.label,
          language: activeTab.language,
          content,
        };
      }
    }
  }

  const uiContext = buildUiContext(state);
  if (uiContext) payload.ui_context = uiContext;

  _lastPrompt = fullMessage;
  // Hosted: the run socket frame is {message} (+ the picked model when the catalog picker is
  // active; connection + conversation are in the URL and owned server-side). Local/desktop
  // keeps the rich {type:"chat", …} payload.
  if (getAuthMode() === "hosted") {
    const model = selectedModel();
    wsSend(model ? { message: fullMessage, model } : { message: fullMessage });
    // The sandbox worker can take a few seconds to cold-start before its first agent_status
    // reaches us; show the activity shell + an initial status NOW so the run never looks dead.
    const convId = state.activeSessionId;
    ensureActivityShell(convId, ensureAssistantBubble(convId), cancelInFlight);
    setStatus(convId, "Starting the agent…");
  } else {
    wsSend(payload);
  }
  state.isThinking = true;
  renderThinking();
  scrollChat();
  onSessionRunning(state.activeSessionId);
  getOrCreateSession(state.activeSessionId).lastSeq = 0; // a fresh run restarts its seq at 1
  armRunWatchdog(state.activeSessionId); // C2: recover the UI if no event ever arrives
  // E2E signals: __cpqSawFinal clears on every dispatch so it means "the run
  // just started finished" (its agent_final sets it), not "any run ever did".
  window.__cpqSawFinal = false;
  window.__cpqTurnsDispatched = (window.__cpqTurnsDispatched || 0) + 1;
}

// ---------------------------------------------------------------------------
// Regenerate answer + edit-and-resend
// ---------------------------------------------------------------------------

// Rebuild the active session's transcript from a truncated messages array,
// keeping only indices 0..keepIndex. Reuses loadConversationMessages (resets
// state.messages then re-renders the DOM for the active session) so the
// transcript DOM stays in lock-step with the model. Returns the kept messages.
function _truncateActiveSessionTo(keepIndex) {
  const kept = truncateMessagesAfter(state.messages, keepIndex);
  loadConversationMessages(kept);
  // An intentional history rewrite: replace the stored transcript to match (the
  // routine turn persists are appends and can never express a truncation).
  _persistConversationMessages();
  return kept;
}

// Regenerate: drop the last assistant answer (and anything after the last user
// message) and re-run that user prompt to produce a fresh answer. The user
// bubble is kept — only the stale answer is replaced.
function regenerateLastAnswer(convId) {
  if (convId && convId !== state.activeSessionId) return; // button lives in the active bubble
  if (state.inputLocked) {
    notify("Agent is busy — wait for it to finish before regenerating", "info");
    return;
  }
  const uIdx = lastUserIndex(state.messages);
  if (uIdx < 0) return;
  const src = state.messages[uIdx];
  const prompt = (src && (src.prompt || src.content) || "").trim();
  if (!prompt) return;
  resetRun();
  _truncateActiveSessionTo(uIdx);  // keep the user message, drop its stale answer
  _dispatchTurn(prompt);
}

// Edit-and-resend: rewrite a prior user message's text, TRUNCATE the transcript
// after it (dropping the stale answer + every later turn), and run the edited
// prompt. Invoked by chat.js's inline editor via window.__chatResendEditedMessage.
function editAndResend(msgId, newText) {
  if (state.inputLocked) {
    notify("Agent is busy — wait for it to finish before resending", "info");
    return;
  }
  const idx = state.messages.findIndex((m) => m.id === msgId);
  if (idx < 0 || state.messages[idx].role !== "user") return;
  const text = (newText || "").trim();
  if (!text) return;
  resetRun();
  // Rewrite the edited message in place (content = display, prompt = backend).
  state.messages[idx] = { ...state.messages[idx], content: text, prompt: text };
  _truncateActiveSessionTo(idx);   // keep the edited message, drop everything after
  _dispatchTurn(text);
}

// Programmatically send a prepared prompt as a user turn into the ACTIVE
// conversation (used by the assistant drawer's Explain/Improve/Apply). Renders
// the user bubble and streams the reply through the normal path. Guarded like
// sendMessage: no-op while a run is in flight.
export function sendComposerText(text) {
  if (state.inputLocked) return false;
  const t = (text || "").trim();
  if (!t) return false;
  resetRun();
  addUserMessage(t);
  const sent = state.messages[state.messages.length - 1];
  if (sent) sent.prompt = t;
  _dispatchTurn(t);
  return true;
}

function updateSendButton() {
  const input = refs.promptInput || document.getElementById('promptInput');
  const btn = refs.btnSend || document.getElementById('btnSend');
  if (btn && input) {
    const hasAttachments = (state.pendingAttachments || []).length > 0;
    btn.disabled = state.inputLocked || (!input.value.trim() && !hasAttachments);
  }
}

// ---------------------------------------------------------------------------
// File attachments (DOCX + image uploads)
// ---------------------------------------------------------------------------

const IMAGE_MIME_TYPES = ["image/png", "image/jpeg", "image/jpg"];
const MAX_ATTACH_BYTES = 10 * 1024 * 1024;  // 10 MB — matches server image cap
const MAX_TEXT_BYTES = 5 * 1024 * 1024;     // 5 MB — matches server text cap

function isImageFile(file) {
  if (!file) return false;
  const t = (file.type || "").toLowerCase();
  if (IMAGE_MIME_TYPES.includes(t)) return true;
  // Some browsers don't set type for pasted blobs — fall back to extension
  const n = (file.name || "").toLowerCase();
  return n.endsWith(".png") || n.endsWith(".jpg") || n.endsWith(".jpeg");
}

function isDocxFile(file) {
  return !!file && (file.name || "").toLowerCase().endsWith(".docx");
}

function isMarkdownFile(file) {
  if (!file) return false;
  const n = (file.name || "").toLowerCase();
  if (n.endsWith(".md") || n.endsWith(".markdown")) return true;
  const t = (file.type || "").toLowerCase();
  return t === "text/markdown" || t === "text/x-markdown";
}

async function handleFileAttach() {
  const fileInput = document.getElementById("fileAttachInput");
  if (!fileInput || !fileInput.files.length) return;

  const files = Array.from(fileInput.files);
  for (const file of files) {
    if (isImageFile(file)) {
      await attachImage(file);
    } else if (isDocxFile(file)) {
      await attachDocx(file);
    } else if (isMarkdownFile(file)) {
      await attachText(file);
    } else {
      addAssistantMessage(`Unsupported file type: ${file.name}. Allowed: .docx, .png, .jpg, .jpeg, .md`);
    }
  }
  fileInput.value = "";
}

async function attachDocx(file) {
  if (file.size > MAX_ATTACH_BYTES) {
    addAssistantMessage(`File too large: ${file.name} (max 10 MB)`);
    return;
  }
  // Only one DOCX at a time (template flow)
  const existing = (state.pendingAttachments || []).find(a => a.kind === "docx");
  if (existing) {
    addAssistantMessage("A DOCX template is already attached. Remove it before attaching another.");
    return;
  }
  const placeholder = _addPlaceholderChip(`Uploading ${file.name}...`);
  try {
    const result = await api.uploadDocx(file);
    const att = { ...result, kind: "docx" };
    state.pendingAttachments = [...(state.pendingAttachments || []), att];
    _removePlaceholder(placeholder);
    renderAttachments();
    updateSendButton();
  } catch (e) {
    _removePlaceholder(placeholder);
    renderAttachments();
    addAssistantMessage(`Upload failed: ${e.message}`);
  }
}

async function attachText(file) {
  if (file.size > MAX_TEXT_BYTES) {
    addAssistantMessage(`File too large: ${file.name} (max 5 MB)`);
    return;
  }
  const placeholder = _addPlaceholderChip(`Uploading ${file.name}...`);
  try {
    const result = await api.uploadText(file);
    const att = {
      kind: "text",
      format: result.format || "markdown",
      filename: result.filename || file.name,
      path: result.path,
      size: result.size,
      content: result.content || "",
    };
    state.pendingAttachments = [...(state.pendingAttachments || []), att];
    _removePlaceholder(placeholder);
    renderAttachments();
    updateSendButton();
  } catch (e) {
    _removePlaceholder(placeholder);
    renderAttachments();
    addAssistantMessage(`Upload failed: ${e.message}`);
  }
}

async function attachImage(file) {
  // Hosted: there is no worker-readable image channel yet (the desktop route saves to the
  // control plane's disk, which the sandboxed agent can't read). One guard here covers the
  // picker, paste, and drag-drop paths — they all funnel through attachImage.
  if (getAuthMode() === "hosted") {
    notify("Images are not supported in the hosted app yet. Try a Word (.docx) or Markdown (.md) file, or describe the image in your message.", "info");
    return;
  }
  if (file.size > MAX_ATTACH_BYTES) {
    addAssistantMessage(`Image too large: ${file.name || "(pasted image)"} (max 10 MB)`);
    return;
  }
  // Build a local data URL for the thumbnail before upload completes
  const dataUrl = await _readFileAsDataUrl(file);
  const placeholder = _addPlaceholderChip(`Uploading ${file.name || "image"}...`);
  try {
    const result = await api.uploadImage(file);
    const att = {
      kind: "image",
      filename: result.filename || file.name || "image",
      path: result.path,
      size: result.size,
      media_type: result.media_type,
      dataUrl,
    };
    state.pendingAttachments = [...(state.pendingAttachments || []), att];
    _removePlaceholder(placeholder);
    renderAttachments();
    updateSendButton();
  } catch (e) {
    _removePlaceholder(placeholder);
    renderAttachments();
    addAssistantMessage(`Upload failed: ${e.message}`);
  }
}

function _readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error("read failed"));
    reader.readAsDataURL(file);
  });
}

function _addPlaceholderChip(label) {
  const container = document.getElementById("composerAttachments");
  if (!container) return null;
  container.hidden = false;
  const chip = document.createElement("div");
  chip.className = "attach-chip uploading";
  chip.textContent = label;
  container.appendChild(chip);
  return chip;
}

function _removePlaceholder(chip) {
  if (chip && chip.parentNode) chip.parentNode.removeChild(chip);
}

function renderAttachments() {
  const container = document.getElementById("composerAttachments");
  if (!container) return;
  const atts = state.pendingAttachments || [];
  // Preserve any uploading placeholders that may exist; rebuild only the final chips
  const placeholders = Array.from(container.querySelectorAll(".attach-chip.uploading"));
  container.innerHTML = "";
  for (const att of atts) {
    container.appendChild(_buildChip(att));
  }
  for (const p of placeholders) container.appendChild(p);
  container.hidden = container.children.length === 0;
}

function _buildChip(att) {
  const chip = document.createElement("div");
  chip.className = `attach-chip ${att.kind}`;
  if (att.kind === "image") {
    const sizeKb = att.size ? `${Math.max(1, Math.round(att.size / 1024))} KB` : "";
    chip.innerHTML = `
      <img class="attach-thumb" src="${att.dataUrl || ""}" alt="" />
      <span class="attach-name">${esc(att.filename)}</span>
      <span class="attach-meta">${sizeKb}</span>
      <button class="attach-remove" title="Remove">&times;</button>
    `;
  } else if (att.kind === "docx") {
    const meta = att.structure || {};
    const details = [
      `${att.tag_count} tag${att.tag_count !== 1 ? "s" : ""}`,
      meta.tables ? `${meta.tables} table${meta.tables !== 1 ? "s" : ""}` : null,
    ].filter(Boolean).join(", ");
    chip.innerHTML = `
      <i class="ic ic-file"></i>
      <span class="attach-name">${esc(att.filename)}</span>
      <span class="attach-meta">${details}</span>
      <button class="attach-remove" title="Remove">&times;</button>
    `;
  } else if (att.kind === "text") {
    const sizeKb = att.size ? `${Math.max(1, Math.round(att.size / 1024))} KB` : "";
    const label = att.format === "markdown" ? "MD" : "TXT";
    chip.innerHTML = `
      <span class="attach-text-badge">${label}</span>
      <span class="attach-name">${esc(att.filename)}</span>
      <span class="attach-meta">${sizeKb}</span>
      <button class="attach-remove" title="Remove">&times;</button>
    `;
  }
  chip.querySelector(".attach-remove")?.addEventListener("click", () => removeAttachment(att));
  return chip;
}

function removeAttachment(att) {
  state.pendingAttachments = (state.pendingAttachments || []).filter(a => a !== att);
  renderAttachments();
  updateSendButton();
}

function clearAttachments() {
  state.pendingAttachments = [];
  const container = document.getElementById("composerAttachments");
  if (container) {
    container.hidden = true;
    container.innerHTML = "";
  }
  updateSendButton();
}

function autoResize(textarea) {
  textarea.style.height = "auto";
  textarea.style.height = Math.min(textarea.scrollHeight, 160) + "px";
}

// (Old toggleSidebar / initSidebarResize / switchActivityMode removed — zone layout handles these)

// ---------------------------------------------------------------------------
// Events
// ---------------------------------------------------------------------------

/**
 * Show or hide the Errors activity-bar button based on auth mode and admin status.
 *
 * Security: the real boundary is the backend /api/admin/errors* gate (403). This is UX only.
 * Logic: visible = (local mode) OR (hosted mode AND is_admin confirmed via /api/me).
 * Fail-closed: in hosted mode the button starts hidden (_isAdmin default = false) and is
 * revealed only after /api/me confirms is_admin: true.
 */
function applyErrorsPanelVisibility() {
  const visible = getAuthMode() !== "hosted" || isAdmin();
  const btn = document.querySelector('#activityBar .ab-btn[data-panel="errors"]');
  if (!btn) return;
  btn.style.display = visible ? "" : "none";
  if (!visible) {
    // If the panel is currently active/open, close it so a non-admin can't reach it via
    // a saved layout state that left it visible. hidePanel() is a no-op if not active.
    hidePanel("errors");
  }
}

/**
 * v2 shell only: show or hide the Errors NAV ITEM using the IDENTICAL admin
 * gate as applyErrorsPanelVisibility() above (v1's left-zone panel button) --
 * same security note applies (UX only; the real boundary is the backend
 * /api/admin/errors* gate). v1's DOM (#activityBar) doesn't exist in a v2
 * session (init() returns before any v1 layout/panel wiring runs for the v2
 * branch -- see init() below), so this is a parallel application of the SAME
 * condition against the v2 sidebar's nav item instead of the v1 activity-bar
 * button. v1's applyErrorsPanelVisibility() itself is left untouched.
 *
 * Called once from initV2Shared(), right after cacheRefs(). isAdmin() is
 * already resolved by then: every hosted boot path (auth.js's boot()) fetches
 * /api/me and populates _isAdmin BEFORE initFn() (this module's init(), which
 * calls initV2Shared()) ever runs -- same precondition v1's call site (init(),
 * after _populateErrorsPanel()) already relies on.
 *
 * v1's applyErrorsPanelVisibility() has exactly one call site (init(), v1
 * only) -- it is not re-applied anywhere else (no re-apply on login/logout/
 * tenant switch), so there is nothing else to mirror here beyond the single
 * initV2Shared() call below.
 */
function applyErrorsNavVisibilityV2() {
  const visible = getAuthMode() !== "hosted" || isAdmin();
  const navItem = document.querySelector('#appV2 .v2-nav-item[data-view="errors"]');
  if (navItem) navItem.style.display = visible ? "" : "none";
}

/**
 * Show or hide the Learning activity-bar button based on auth mode and admin status.
 * Learning is hosted-only: unlike Errors, there is no local /api/agent/learning*
 * endpoint, so a local session must never see this button — it would be a
 * guaranteed-to-error dead end rather than a plain UX gate. This is still UX-only
 * (the backend /api/admin/learning* gate is the real, authoritative boundary).
 */
function applyLearningPanelVisibility() {
  const visible = getAuthMode() === "hosted" && isAdmin();
  const btn = document.querySelector('#activityBar .ab-btn[data-panel="learning"]');
  if (!btn) return;
  btn.style.display = visible ? "" : "none";
  if (!visible) {
    // Mirrors applyErrorsPanelVisibility(): a saved layout can't leave the panel
    // reachable for a non-admin. hidePanel() is a no-op if not active.
    hidePanel("learning");
  }
}

/**
 * v2 shell only: show or hide the Learning NAV ITEM using the IDENTICAL hosted-only
 * admin gate as applyLearningPanelVisibility() above (Learning has no local backend,
 * so it must stay hidden outside hosted mode). Mirrors applyErrorsNavVisibilityV2()
 * (see that function's comment for the full rationale — v1's #activityBar DOM
 * doesn't exist in a v2 session).
 */
function applyLearningNavVisibilityV2() {
  const visible = getAuthMode() === "hosted" && isAdmin();
  const navItem = document.querySelector('#appV2 .v2-nav-item[data-view="learning"]');
  if (navItem) navItem.style.display = visible ? "" : "none";
}

/**
 * Show or hide the Promotion activity-bar button based on the plan feature flag.
 *
 * 1.0 tier split: hosted Promotion is reserved for the Max plan ("promotion" in
 * /api/me entitlements.features). Security: the real boundary is the backend 402
 * feature_gated check on /api/connections/{cid}/promotion/* — this is UX only.
 * Local mode always shows the panel (developer tool, no plans concept).
 */
function applyPromotionPanelVisibility() {
  const visible = getAuthMode() !== "hosted" || getUserFeatures().includes("promotion");
  const btn = document.querySelector('#activityBar .ab-btn[data-panel="promotion"]');
  if (!btn) return;
  btn.style.display = visible ? "" : "none";
  if (!visible) {
    // Mirrors applyErrorsPanelVisibility(): a saved layout can't leave the panel
    // reachable for a gated plan. hidePanel() is a no-op if not active.
    hidePanel("promotion");
  }
}

/**
 * v2 shell only: the IDENTICAL plan-feature gate applied to BOTH v2 promotion surfaces —
 * the "Promotion" nav item (the release-ladder wizard) AND the "Deployments" nav item
 * (`data-view="timeline"`, timeline-view.js). Deployments is a promotion-RUNS-ONLY view
 * (deployment cards + Revert over api.promotionRuns()); that route now 402s for every
 * purchasable plan, so left ungated it renders permanently empty for 100% of Free/Pro
 * users. Do NOT extend this to the v2 `audit` nav item (Audit Trail stays free) or to
 * anything in the v1 shell — v1's `timeline` panel is a DIFFERENT thing (v1's
 * audit-trail/change-history panel) and is already hidden in hosted mode via a CSS rule
 * in auth.js; see applyPromotionPanelVisibility() above, which is v1-only and unrelated.
 * (v1's #activityBar DOM doesn't exist in a v2 session — see applyErrorsNavVisibilityV2()
 * for the full rationale.)
 */
function applyPromotionNavVisibilityV2() {
  const visible = getAuthMode() !== "hosted" || getUserFeatures().includes("promotion");
  const selector = '#appV2 .v2-nav-item[data-view="promotion"], #appV2 .v2-nav-item[data-view="timeline"]';
  document.querySelectorAll(selector).forEach((navItem) => {
    navItem.style.display = visible ? "" : "none";
  });
}

/**
 * v2 shell only: hide nav items whose views are desktop-only in hosted mode. Today that
 * is exactly `sync` — git working-tree sync of the local mirror; its /api/sync/* routes
 * are gated in hosted, and the hosted analogue is per-connection Promotion. Knowledge and
 * Promotion stay visible (their hosted surfaces exist). Same UX-only gating shape as
 * applyErrorsNavVisibilityV2() above (display:none removes the item from flow, so the
 * Operate group closes up with no empty slot); the backend gate stays authoritative.
 */
function applyHostedNavVisibilityV2() {
  if (getAuthMode() !== "hosted") return;
  const navItem = document.querySelector('#appV2 .v2-nav-item[data-view="sync"]');
  if (navItem) navItem.style.display = "none";
}

/** Sync `.active` class on activity bar buttons to match active left-zone panel */
function _syncActivityBar() {
  const activeLeft = state.leftZoneVisible ? getActivePanel('left') : null;
  for (const b of document.querySelectorAll('#activityBar .ab-btn[data-panel]')) {
    b.classList.toggle('active', b.dataset.panel === activeLeft);
  }
}

// Wires the tenant switcher: the top-nav <select> (hidden, programmatic-compat),
// the custom dropdown button + outside-click close, and "+ Add tenant...". Shared
// by v1 (called from inside bindEvents(), exactly where this block used to live
// inline) and v2 (called directly from initV2Shared() after cacheRefs() -- v2's
// topbar reuses the SAME element ids, shell-v2.js's _tenantSwitcherHtml()).
// Idempotent-once: only one shell's DOM exists per page load, but bindEvents()
// itself already runs in both boot paths, so this guards against wiring the same
// elements' listeners twice within one page load (v2 calls this directly AND gets
// it again via bindEvents() a few lines later in initV2Shared()).
let _tenantSwitcherWired = false;
function wireTenantSwitcher() {
  if (_tenantSwitcherWired) return;
  _tenantSwitcherWired = true;

  // Both modes wire the card + dropdown here. The dropdown BODY (renderTenantDropdown(),
  // tenants.js) branches on the mode: hosted lists the user's connections, local lists saved
  // tenants. (Hosted previously short-circuited the card to a full page reload back to the
  // boot picker; that stopgap is gone -- the card now opens the same in-place dropdown local uses.)

  // Tenant selector (top-nav <select>, hidden — kept for programmatic compat; absent in
  // hosted mode, so the optional-chaining call below simply no-ops there)
  refs.tenantSelector?.addEventListener("change", (e) => {
    const val = e.target.value;
    if (val === '__add__') {
      openLoginDialog();
      // Reset to previous value
      if (state.tenant) e.target.value = state.tenant;
    } else if (val && val !== state.tenant) {
      switchTenant(val);
    }
  });

  // Custom tenant dropdown button (grouped dropdown + lineage editor)
  refs.tenantSwitcher?.addEventListener("click", (e) => {
    e.stopPropagation();
    toggleTenantDropdown();
  });
  refs.btnAddTenant?.addEventListener("click", () => {
    closeTenantDropdown();
    // Hosted: the in-app add-connection overlay, then switch to the new connection in place.
    // Local: the saved-tenant login dialog (unchanged).
    if (getAuthMode() === "hosted") openAddConnection(switchConnection);
    else openLoginDialog();
  });
  document.addEventListener("click", (e) => {
    if (
      state.tenantDropdownOpen &&
      refs.tenantDropdown &&
      !refs.tenantDropdown.contains(e.target) &&
      e.target !== refs.tenantSwitcher
    ) {
      closeTenantDropdown();
    }
  });
}

function bindEvents() {
  // Activity bar — VS Code-style panel switching for the left zone.
  // Panels are never removed from the zone by activity bar clicks.
  // Click active panel → collapse zone. Click another → switch to it.
  for (const btn of document.querySelectorAll('#activityBar .ab-btn[data-panel]')) {
    btn.addEventListener('click', () => {
      const panelId = btn.dataset.panel;

      // Settings is a modal dialog, not a left-zone panel
      if (panelId === 'settings') {
        openSettingsDialog();
        return;
      }

      const activeLeft = getActivePanel('left');

      if (!state.leftZoneVisible) {
        // Zone collapsed → expand and show requested panel
        expandZone('left');
        showPanel(panelId);
      } else if (activeLeft === panelId) {
        // Already active → collapse the zone (don't hide the panel)
        collapseZone('left');
      } else {
        // Different panel → just switch to it
        showPanel(panelId);
      }
      _syncActivityBar();
      // Lazy-load panel content on first show (only if panel is now visible)
      const panelEl = document.getElementById(`panel-${panelId}`);
      const panelVisible = panelEl && !panelEl.hidden;
      if (panelId === 'exec-order' && panelVisible) {
        // The boot-time initFormulaPopup() ran while #pipelineBody was still
        // detached (panels hidden by default), so it never wired -- latch it
        // here on first show, same idea as knowledge's _kbInited below.
        initFormulaPopup();
        if (!state.fieldsTree && !state.pipelineData) loadPipeline();
      }
      if (panelId === 'knowledge' && panelVisible) {
        // Re-init event listeners if not yet wired (panel was detached from DOM at boot)
        if (!panelEl._kbInited) {
          initKnowledgePanel();
          panelEl._kbInited = true;
        }
        onKnowledgeActivated();
      }
      if (panelId === 'db-inspector' && panelVisible) {
        if (!panelEl._dbInited) {
          initDbInspector();
          panelEl._dbInited = true;
        }
      }
      if (panelId === 'sync-status' && panelVisible) {
        renderSyncPanel();
        // Auto-fetch sync data if not yet loaded
        if (!state.syncStatuses || state.syncStatuses.length === 0) {
          loadSyncStatuses(true);
        }
      }
      if (panelId === 'timeline' && panelVisible) {
        if (!panelEl._tlInited) {
          initTimelinePanel();
          panelEl._tlInited = true;
        }
        onTimelineActivated();
      }
      if (panelId === 'traces' && panelVisible) {
        if (!panelEl._tracesInited) {
          initTracesPanel();
          panelEl._tracesInited = true;
        }
        onTracesActivated();
      }
      if (panelId === 'promotion' && panelVisible) {
        if (!panelEl._promInited) {
          initPromotionPanel();
          panelEl._promInited = true;
        }
        onPromotionActivated();
      }
      if (panelId === 'errors' && panelVisible) {
        if (!panelEl._errorsInited) {
          initErrorsPanel();
          panelEl._errorsInited = true;
        }
        onErrorsActivated();
      }
      if (panelId === 'learning' && panelVisible) {
        if (!panelEl._learningInited) {
          initLearningPanel();
          panelEl._learningInited = true;
        }
        onLearningActivated();
      }
      if (panelId === 'pipelines' && panelVisible) {
        if (!panelEl._plInited) {
          initPipelinesPanel();
          panelEl._plInited = true;
        }
        onPipelinesActivated();
      }
    });
  }
  // (Legacy switchActivityMode listener removed — panel-registry handles all activity bar buttons)

  // (Pipeline scope-tab + Refresh wiring moved into _populateExecOrderPanel(): the
  // exec-order panel is HIDDEN_BY_DEFAULT, so its DOM is detached from the document
  // at bindEvents() time and document-level queries here matched zero nodes — the
  // tabs and Refresh were never wired. Container-scoped wiring at populate time
  // sees the detached tree.)
  // (Pull All Calcs button removed — sync actions live in the diff panel)

  // Conversation history
  refs.btnNewConversation?.addEventListener("click", () => startNewConversation());
  refs.btnSaveConversation?.addEventListener("click", () => saveCurrentConversation());

  // (Old sidebar collapse listener removed — zone layout handles left zone toggle)

  // Sync refresh
  refs.btnRefreshSync?.addEventListener("click", async () => {
    await loadSyncStatuses(true);
    showDiffPanel();
  });

  // (Stash All + sync summary listeners removed — bulk sync actions live in the diff panel)

  // (Old btnOpenSearch listener removed — global search button in top nav is wired below)

  // File attachment (button + drag-and-drop + paste)
  const btnAttach = document.getElementById("btnAttach");
  const fileInput = document.getElementById("fileAttachInput");
  if (btnAttach && fileInput) {
    btnAttach.addEventListener("click", () => fileInput.click());
    fileInput.addEventListener("change", handleFileAttach);
    // Allow multi-select and accept DOCX + common image formats + markdown. Hosted has no
    // image channel to the worker yet, so its OS picker never offers images (attachImage
    // still guards the paste/drag paths with a toast).
    fileInput.setAttribute("accept", getAuthMode() === "hosted"
      ? ".docx,.md,.markdown"
      : ".docx,image/png,image/jpeg,image/jpg,.md,.markdown");
    fileInput.setAttribute("multiple", "multiple");
  }
  // Drag-and-drop onto composer — DOCX or images
  const composer = document.querySelector(".composer-inner");
  if (composer) {
    composer.addEventListener("dragover", (e) => {
      e.preventDefault();
      composer.classList.add("drag-over");
    });
    composer.addEventListener("dragleave", () => composer.classList.remove("drag-over"));
    composer.addEventListener("drop", async (e) => {
      e.preventDefault();
      composer.classList.remove("drag-over");
      const files = Array.from(e.dataTransfer?.files || []);
      for (const file of files) {
        if (isImageFile(file)) {
          await attachImage(file);
        } else if (isDocxFile(file)) {
          await attachDocx(file);
        } else if (isMarkdownFile(file)) {
          await attachText(file);
        }
      }
    });
  }

  // Paste handler — pick up image/* clipboard items and attach them.
  // Text paste flows through to the textarea natively.
  refs.promptInput?.addEventListener("paste", async (e) => {
    const items = Array.from(e.clipboardData?.items || []);
    const imageItems = items.filter(it => it.kind === "file" && (it.type || "").toLowerCase().startsWith("image/"));
    if (imageItems.length === 0) return;
    e.preventDefault();
    for (const item of imageItems) {
      const file = item.getAsFile();
      if (file) await attachImage(file);
    }
  });

  // Generic composer hand-off: other modules (editor save intercept, chat code blocks,
  // template scaffold) dispatch this to pre-fill a prompt without importing main.js —
  // the same CustomEvent seam the editor selection actions use. Wired here (bindEvents
  // runs in BOTH shells' boot paths) so the hand-off works in v1 and v2 alike.
  window.addEventListener("agent-prefill-composer", (e) => prefillComposer(e.detail && e.detail.text));

  // Chat send
  refs.btnSend?.addEventListener("click", sendMessage);
  refs.promptInput?.addEventListener("keydown", (e) => {
    if (handleAtKeydown(e)) return;
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  });
  refs.promptInput?.addEventListener("input", () => {
    const input = refs.promptInput || document.getElementById('promptInput');
    if (input) autoResize(input);
    updateSendButton();
    handleAtInput();
  });
  refs.promptInput?.addEventListener("blur", () => {
    hideAutocomplete();
  });

  // Welcome chips
  for (const chip of $$(".chip[data-prompt]")) {
    chip.addEventListener("click", () => {
      const input = refs.promptInput || document.getElementById('promptInput');
      if (input) input.value = chip.dataset.prompt;
      updateSendButton();
      sendMessage();
    });
  }

  // Configurator option chips (legacy table)
  const chatStreamEl = refs.chatStream || document.getElementById('chatStream');
  chatStreamEl?.addEventListener("click", (e) => {
    const opt = e.target.closest(".cfg-opt");
    if (!opt) return;
    const attrName = opt.dataset.attr;
    const value = opt.dataset.val;
    const input = refs.promptInput || document.getElementById('promptInput');
    if (attrName && value && input) {
      input.value = `set ${attrName} = ${value}`;
      updateSendButton();
      input.focus();
    }
  });

  // Diff panel — template + close button wired by _ensureDiffPanelContent() in diff-panel.js

  // Status bar sync indicator — click opens sync panel
  document.getElementById('sbSyncIndicator')?.addEventListener('click', () => focusSyncPanel());

  // Top-nav sync badges — click opens sync panel with matching group expanded
  document.getElementById('syncBadgeSync')?.addEventListener('click', () => focusSyncPanel('in_sync'));
  document.getElementById('syncBadgeLocal')?.addEventListener('click', () => focusSyncPanel('local_only'));
  document.getElementById('syncBadgeRemote')?.addEventListener('click', () => focusSyncPanel('remote_only'));
  document.getElementById('syncBadgeConflict')?.addEventListener('click', () => focusSyncPanel('conflict'));

  // Confirm dialog
  const claimConfirm = () => {
    const r = refs.confirmDialog._currentResolve;
    refs.confirmDialog._currentResolve = null;
    return r;
  };
  refs.confirmCancel.addEventListener("click", () => {
    const r = claimConfirm();
    refs.confirmDialog.close();
    r?.(false);
  });
  refs.confirmOk.addEventListener("click", () => {
    const r = claimConfirm();
    refs.confirmDialog.close();
    r?.(true);
  });
  refs.confirmDialog.addEventListener("close", () => {
    // Esc/backdrop resolve false. dialog.close() queues this event async, so a
    // previous dialog's close can land AFTER a new confirm() has already
    // reopened the dialog and installed its resolver — claim-and-null alone
    // can't tell them apart. But if the dialog is open again, this event is
    // stale by definition (the live session isn't closed): skip it.
    if (refs.confirmDialog.open) return;
    claimConfirm()?.(false);   // genuine close; button paths already claimed → no-op
  });

  // Typed-confirm dialog (confirmTyped() in dialogs.js) — same per-call
  // claim-and-null resolver + stale-close guard as the plain confirm dialog
  // just above (see its "close" handler comment for why the open-guard is
  // required). OK also stays disabled until the typed value matches the
  // token confirmTyped() stashed on the dialog element.
  const claimConfirmTyped = () => {
    const r = refs.confirmTypedDialog._currentResolve;
    refs.confirmTypedDialog._currentResolve = null;
    return r;
  };
  refs.confirmTypedInput.addEventListener("input", () => {
    refs.confirmTypedOk.disabled =
      refs.confirmTypedInput.value.trim() !== (refs.confirmTypedDialog._expectedToken || "");
  });
  refs.confirmTypedCancel.addEventListener("click", () => {
    const r = claimConfirmTyped();
    refs.confirmTypedDialog.close();
    r?.(false);
  });
  refs.confirmTypedOk.addEventListener("click", () => {
    // Belt-and-suspenders: enforcement must not rest on the disabled attribute
    // alone -- a synthetic dispatchEvent (or a future Enter-key path) reaches
    // this listener regardless of disabled state (timeline-view.js's _onRevert
    // has the same "disabled guard" comment for the same reason). The caller's
    // confirm value is derived from already-known state, not re-read from this
    // input (see timeline-view.js's _onRevert), so the UI always sends an
    // already-correct confirm value -- the server gate can't catch a defeated
    // disabled-gate on its own. Re-validate the typed token independently
    // before claiming/resolving.
    if (refs.confirmTypedInput.value.trim() !== (refs.confirmTypedDialog._expectedToken || "")) return;
    const r = claimConfirmTyped();
    refs.confirmTypedDialog.close();
    r?.(true);
  });
  refs.confirmTypedDialog.addEventListener("close", () => {
    if (refs.confirmTypedDialog.open) return;
    claimConfirmTyped()?.(false);
  });

  wireTenantSwitcher();

  // Login dialog
  refs.loginCancel.addEventListener("click", () => refs.loginDialog.close());
  refs.loginSubmit.addEventListener("click", submitLogin);
  refs.loginPassword.addEventListener("keydown", (e) => {
    if (e.key === "Enter") submitLogin();
  });
  refs.loginClientSecret.addEventListener("keydown", (e) => {
    if (e.key === "Enter") submitLogin();
  });
  // Auth-method segmented toggle -- wired ONCE here (not inside openLoginDialog, which
  // runs on every open against this same static dialog markup; re-attaching there would
  // stack duplicate listeners).
  refs.loginAuthMode.querySelectorAll("button").forEach((b) =>
    b.addEventListener("click", () => setLoginAuthMode(b.dataset.mode)));

  // Global search button in top nav
  document.getElementById('btnGlobalSearch')?.addEventListener('click', () => openSearch());

  // View menu button in top nav
  document.getElementById('btnViewMenu')?.addEventListener('click', (e) => {
    e.stopPropagation();
    toggleViewMenu();
  });

  // Settings button in top nav — opens the settings modal
  document.getElementById('btnSettings')?.addEventListener('click', () => openSettingsDialog());

  // Status bar shortcuts button — opens the keyboard shortcuts dialog
  document.getElementById('statusbarShortcutsBtn')?.addEventListener('click', () => {
    openShortcutsDialog();
  });

  // "Reset Layout" action lives inside the settings modal now
  window.addEventListener('settings-reset-layout', async () => {
    const ok = await confirm('Reset Layout', 'Reset all panel positions and zone sizes to their defaults?');
    if (ok) { resetLayout(); location.reload(); }
  });

  // Global keyboard shortcuts
  document.addEventListener('keydown', (e) => {
    const meta = e.metaKey || e.ctrlKey;

    // Don't intercept when typing in inputs/textareas (except specific shortcuts)
    const isInput = e.target.matches('input, textarea, select, [contenteditable]');

    // Cmd+B → toggle left zone (sidebar)
    if (meta && !e.shiftKey && e.key === 'b' && !isInput) {
      e.preventDefault();
      toggleZone('left');
    }

    // Cmd+J → toggle bottom zone (console)
    if (meta && !e.shiftKey && e.key === 'j' && !isInput) {
      e.preventDefault();
      toggleZone('bottom');
    }

    // Cmd+Shift+P → open View menu
    if (meta && e.shiftKey && (e.key === 'P' || e.key === 'p')) {
      e.preventDefault();
      toggleViewMenu();
    }

    // Cmd+Shift+F → global find-in-files
    if (meta && e.shiftKey && (e.key === 'F' || e.key === 'f')) {
      e.preventDefault();
      openSearch();
    }

    // Cmd+S → save active file tab (global, works from anywhere)
    if (meta && !e.shiftKey && (e.key === 's' || e.key === 'S') && state.activeWorkspaceTab) {
      e.preventDefault();
      saveCurrentTab();
    }

    // Escape → blur current input
    if (e.key === 'Escape' && isInput) {
      e.target.blur();
    }

    // Cmd+Enter → run script in Script Workbench (editor only — never hijack chat send)
    if (meta && !e.shiftKey && e.key === 'Enter' && state.activeWorkspaceTab && !isInput) {
      e.preventDefault();
      runActiveScriptInWorkbench();
    }

    // Cmd+Shift+V → validate syntax (skip when typing in inputs)
    if (meta && e.shiftKey && (e.key === 'V' || e.key === 'v') && state.activeWorkspaceTab && !isInput) {
      e.preventDefault();
      validateActiveTab();
    }

    // Cmd+Shift+M → toggle editor overflow menu (skip when typing in inputs)
    if (meta && e.shiftKey && (e.key === 'M' || e.key === 'm') && state.activeWorkspaceTab && !isInput) {
      e.preventDefault();
      openOverflowMenuForActiveTab();
    }

    // Cmd+/ → show keyboard shortcuts dialog
    if (meta && e.key === '/') {
      e.preventDefault();
      openShortcutsDialog();
    }
  });
}

// ---------------------------------------------------------------------------
// Explorer panel (left zone)
// ---------------------------------------------------------------------------

function _populateExplorerPanel() {
  const explorerPanel = getPanelContainer('explorer');
  if (!explorerPanel) return;

  explorerPanel.innerHTML = `
    <div class="explorer-panel">
      <div class="explorer-header">
        <span class="explorer-title">Explorer</span>
        <button class="btn-icon explorer-collapse" id="btnCollapseLeft" title="Collapse sidebar">
          <i class="ic ic-chevron-left"></i>
        </button>
      </div>
      <div class="explorer-search">
        <input type="text" id="explorerFilter" class="explorer-filter-input" placeholder="Filter files..." autocomplete="off" spellcheck="false" />
      </div>
      <div class="file-tree" id="fileTree">
        <div class="tree-placeholder">Loading files...</div>
      </div>
    </div>
  `;

  // Wire explorer-specific events
  document.getElementById('btnCollapseLeft')?.addEventListener('click', () => toggleZone('left'));
  document.getElementById('explorerFilter')?.addEventListener('input', (e) => {
    filterFileTree(e.target.value);
  });

  // Re-cache refs that were just created dynamically
  refs.fileTree = document.getElementById('fileTree');
}

// ---------------------------------------------------------------------------
// Execution Order panel (left zone, hidden by default)
// ---------------------------------------------------------------------------

function _populateExecOrderPanel() {
  const execPanel = getPanelContainer('exec-order');
  if (!execPanel) return;

  execPanel.innerHTML = `
    <div class="exec-order-panel">
      <div class="pipeline-header">
        <h3 class="pipeline-title">Execution Order</h3>
        <div class="pipeline-actions">
          <button class="btn btn-xs" id="btnRefreshPipeline" title="Refresh execution order from cloud">Refresh</button>
        </div>
      </div>
      <div class="pipeline-scope">
        <button class="tab active" data-scope="item-calcs" id="tabItemCalcs">Item Calculations</button>
        <button class="tab" data-scope="quote-events" id="tabQuoteEvents">Quote Events</button>
      </div>
      <div class="pipeline-body" id="pipelineBody">
        <div class="pipeline-placeholder">Click Refresh to load execution order</div>
      </div>
    </div>
  `;

  // Wire the scope tabs + Refresh here, scoped to the panel container. This panel is
  // HIDDEN_BY_DEFAULT (panel-registry), so at bindEvents() time its DOM is detached and
  // document-level queries find none of these nodes — container-scoped queries see the
  // detached tree, and this populate runs exactly once per page load.
  execPanel.querySelectorAll('.pipeline-scope .tab').forEach(tab => {
    tab.addEventListener('click', () => switchPipelineScope(tab.dataset.scope));
  });
  execPanel.querySelector('#btnRefreshPipeline')
    ?.addEventListener('click', () => loadPipeline());
}

// ---------------------------------------------------------------------------
// Knowledge panel (left zone, hidden by default)
// ---------------------------------------------------------------------------

function _populateKnowledgePanel() {
  const kbPanel = getPanelContainer('knowledge');
  if (!kbPanel) return;

  kbPanel.innerHTML = `
    <div class="knowledge-panel-inner">
      <div class="kb-header">
        <div class="kb-tabs">
          <button class="kb-tab-btn active" data-tab="classes">Classes</button>
          <button class="kb-tab-btn" data-tab="events">Events</button>
          <button class="kb-tab-btn" data-tab="patterns">Patterns</button>
        </div>
        <input type="text" id="kbSearchInput" class="kb-search" placeholder="Search..." />
      </div>
      <div class="kb-tab-body" data-tab="classes">
        <div class="kb-list" id="kbClassesList"></div>
      </div>
      <div class="kb-tab-body" data-tab="events" hidden>
        <div class="kb-filters">
          <select id="kbScopeFilter" class="kb-filter-select">
            <option value="">All scopes</option>
            <option value="quote">Quote</option>
            <option value="product">Product</option>
            <option value="container">Container</option>
            <option value="xml">XML Serialization</option>
            <option value="user">User</option>
          </select>
        </div>
        <div class="kb-list" id="kbEventsList"></div>
      </div>
      <div class="kb-tab-body" data-tab="patterns" hidden>
        <div class="kb-filters">
          <select id="kbCategoryFilter" class="kb-filter-select">
            <option value="">All categories</option>
            <option value="sql">SQL / Custom Tables</option>
            <option value="rest_api">REST API</option>
            <option value="quote">Quote</option>
            <option value="product">Product</option>
            <option value="container">Container</option>
            <option value="integration">Integration</option>
            <option value="utility">Utility</option>
          </select>
        </div>
        <div class="kb-list" id="kbPatternsList"></div>
      </div>
    </div>
  `;
}

// ---------------------------------------------------------------------------
// DB Inspector panel (left zone, hidden by default)
// ---------------------------------------------------------------------------

function _populateDbInspectorPanel() {
  const panel = getPanelContainer('db-inspector');
  if (!panel) return;

  panel.innerHTML = `
    <div class="db-inspector-panel">
      <div class="db-inspector-header">
        <h3 class="db-inspector-title">DB Inspector</h3>
        <div class="db-inspector-actions">
          <button class="btn btn-xs" id="btnRefreshTables" title="Refresh table list">Refresh</button>
        </div>
      </div>
      <input type="text" id="dbSearchInput" class="db-search-input" placeholder="Filter tables..." />
      <div class="db-tables-recent" id="dbTablesRecent"></div>
      <div class="db-tables-list" id="dbTablesList">
        <div class="db-tables-placeholder">Click the DB Inspector icon to load tables</div>
      </div>
    </div>
  `;
}

// ---------------------------------------------------------------------------
// Chat panel (right zone)
// ---------------------------------------------------------------------------

// Default #autonomyChip click behavior (v1's only behavior; v2's until
// _buildComposerV2() upgrades it): just re-resolve and re-render. A NAMED
// function (not the inline arrow the shared bind() callback above used to
// use) so _buildComposerV2() can removeEventListener() this EXACT reference
// and install its own interactive toggle handler in its place -- swapping a
// listener by reference, not touching wireChatPanel() itself beyond this
// one-line rename, so v1 (which never calls _buildComposerV2()) keeps this
// exact behavior unchanged.
function _autonomyChipDefaultClick() { refreshAutonomy(); }

function _populateChatPanel() {
  const chatPanel = getPanelContainer('chat');
  if (!chatPanel) return;

  chatPanel.innerHTML = chatPanelMarkup();
  wireChatPanel();
}

// Post-markup chat wiring: autonomy chip, agent dock, backend picker, history
// dropdown. Split out of _populateChatPanel() so the v2 shell -- which builds
// the identical chatPanelMarkup() DOM itself in shell-v2.js's bootShellV2()
// (not through the v1 panel registry) -- can run the SAME wiring from
// initV2Shared() without calling getPanelContainer('chat') (that would try to
// (re)create a v1 zone panel that no longer exists once bootShellV2() has
// removed #app). v1's _populateChatPanel() call site and behavior above are
// unchanged: same statements, same order, just split at this boundary.
function wireChatPanel() {
  // Re-cache refs for dynamically created chat elements
  refs.chatStream = document.getElementById('chatStream');
  refs.chatWelcome = document.getElementById('chatWelcome');
  refs.promptInput = document.getElementById('promptInput');
  refs.btnSend = document.getElementById('btnSend');
  refs.btnNewConversation = document.getElementById('btnNewConversation');
  refs.atAutocomplete = document.getElementById('atAutocomplete');

  // Autonomy chip — reflects /api/agent/status reactively. Shows, in the user's
  // eyeline at the moment they send, whether the agent edits locally (they
  // approve pushes) or pushes to the live cloud. v1: click just re-checks
  // (refreshAutonomy(), a GET) -- unchanged. v2 (M10 T1) upgrades this to a
  // real toggle control after relocating the chip into the composer card;
  // _buildComposerV2() swaps the click listener below for its own (by
  // reference -- see _autonomyChipDefaultClick's own comment), v1 never runs
  // that code path so its behavior here is untouched.
  const autonomyChip = document.getElementById('autonomyChip');
  if (autonomyChip) {
    bind(autonomyChip, autonomyAtom, (s, el) => {
      const vm = describeAutonomy(s);
      el.className = `autonomy-chip tone-${vm.tone}`;
      el.title = vm.tooltip;
      const dot = el.querySelector('.autonomy-dot');
      const label = el.querySelector('.autonomy-label');
      if (dot) dot.className = `autonomy-dot dot-${vm.dot}`;
      if (label) label.textContent = vm.label;
    });
    autonomyChip.addEventListener('click', _autonomyChipDefaultClick);
  }

  // Mount the agent dock (live plan + legible tool timeline) above the composer.
  initAgentDock({ onCancel: cancelInFlight });

  // Wire collapse button
  document.getElementById('btnCollapseRight')?.addEventListener('click', () => toggleZone('right'));

  // Wire backend picker
  wireBackendPicker();

  // Wire history dropdown
  document.getElementById('btnChatHistory')?.addEventListener('click', async () => {
    const dd = document.getElementById('historyDropdown');
    if (!dd) return;
    if (!dd.hidden) { dd.hidden = true; return; }

    // Fetch and render conversations
    try {
      const convos = await api.listConversations();
      const list = convos.conversations || [];
      dd.innerHTML = list.length === 0
        ? '<div class="history-empty">No saved conversations</div>'
        : list.map(c => `
            <button class="history-item" data-id="${c.id}">
              <span class="history-item-title">${esc(c.title || 'Untitled')}</span>
              <span class="history-item-date">${c.date ? _formatDate(c.date) : ''}</span>
            </button>
          `).join('');
      dd.hidden = false;

      // Wire click handlers
      dd.querySelectorAll('.history-item').forEach(item => {
        item.addEventListener('click', async () => {
          dd.hidden = true;
          await loadHistoryConversation(item.dataset.id);
        });
      });
    } catch (e) {
      console.warn('Failed to load conversations:', e);
    }
  });

  // Close history dropdown when clicking outside
  document.addEventListener('click', (e) => {
    const dd = document.getElementById('historyDropdown');
    if (dd && !dd.hidden && !e.target.closest('.chat-panel-header')) {
      dd.hidden = true;
    }
  });
}

// M9 T4 -- composer card (model button, hint, attach, round send), ported
// from the design mock (docs/superpowers/specs/2026-07-01-webchat-redesign-
// mock.html ~775-788/184-198). v2-ONLY DOM surgery, called once from
// initV2Shared() below, right after wireChatPanel() -- chat-markup.js itself
// is never touched, so v1 (which renders the identical shared template via
// its own _populateChatPanel() call above) stays byte-identical. Same
// "relocate the real element, don't clone" pattern as #agentDock/#autonomyChip's
// own M9 T3 precedent in shell-v2.js's bootShellV2().
//
// Ordering note: this MUST run after wireChatPanel() (which calls
// wireBackendPicker() synchronously) so the backend picker's click listener
// and its closured btn/menu/labelEl references are already attached to the
// real #btnBackendPicker/#backendMenu/#backendPickerLabel nodes before this
// function reparents/renames them -- reparenting or renaming a DOM node never
// detaches its listeners or invalidates a captured reference to it. The one
// OTHER place that reads "#btnBackendPicker" by id (wireBackendPicker()'s own
// document-level click-outside guard, main.js `e.target.closest("#btnBackendPicker")`)
// is unreachable dead code regardless of the rename: the button's own click
// handler calls e.stopPropagation() first, so that document-level listener
// never even sees a click that originated on/inside the button. Left as-is
// (unrelated, shared code) rather than "fixed" for an id-rename that changes
// no observable behavior.
// M10 T1 -- composer autonomy toggle. Reads current state from autonomyAtom
// (the SAME source of truth wireChatPanel()'s bind() already renders the chip
// from) rather than re-fetching -- the chip's own visible label is already
// the freshest resolved value. Guards against the two states where a click
// must be a safe no-op: unresolved (loading/error -- nothing safe to toggle;
// the chip itself isn't disabled in this transient case, but there is no
// verified current value to flip FROM) and tenant_md-pinned (disabled in the
// DOM by the effect() below, but this guard is defense-in-depth in case this
// handler is ever invoked another way). Switching INTO autopilot is gated by
// a real, hardened confirm() (dialogs.js's <dialog>-based flow, not
// window.confirm()) naming the actual tenant; switching back to local_only
// needs no confirmation (per the task's own spec -- leaving the higher-risk
// mode is never friction). On success, applies the POST's own returned
// status payload directly (applyAutonomyStatus()) rather than firing a
// separate refreshAutonomy() GET afterward -- see that function's own
// comment (agent-store.js) for why a second round trip would be both
// redundant and racy here.
async function _onAutonomyToggleClick() {
  const s = autonomyAtom.peek();
  if (s.status !== "ok" || !s.autonomy) return;
  if (s.autonomy_source === "tenant_md") return;
  const next = s.autonomy === "autopilot" ? "local_only" : "autopilot";
  if (s.autonomy === "local_only") {
    const tenantLabel = s.tenant_host || s.tenant || "the live tenant";
    const ok = await confirm(
      "Enable autopilot?",
      `The agent will push changes to ${tenantLabel} WITHOUT asking for approval per change.`,
    );
    if (!ok) return;
  }
  try {
    const body = await api.setAutonomy(next);
    applyAutonomyStatus(body);
  } catch (e) {
    console.warn("Failed to set autonomy:", e);
  }
}

function _buildComposerV2() {
  const composer = document.querySelector("footer.composer");
  const btnModel = document.getElementById("btnBackendPicker");
  const btnSend = document.getElementById("btnSend");
  if (!composer || !btnModel || !btnSend) return; // defensive; the v2 DOM always has these

  // Relocate (not clone) the real backend/model selector -- same element,
  // same wireBackendPicker() click handler + closured references, new
  // id/class/home. #backendPickerLabel (the text span wireBackendPicker()
  // writes into) keeps its own id unchanged.
  btnModel.id = "v2CmpModel";
  btnModel.classList.add("cmp-model");

  // Hosted model picker (#modelSelect): index.html's original node lives
  // inside v1's static #app shell, which bootShellV2() (shell-v2.js) removes
  // wholesale before this function ever runs -- initModelPicker() has never
  // been called for v2 (this file's init() only reaches that call in its
  // v1-only tail, after the v2 branch's early return), so no state was ever
  // bound to the original node. A fresh equivalent element (same id/classes,
  // starts hidden like the original) is functionally identical to
  // "relocating" it here, with far less risk than rescuing the exact node out
  // of a tree that's already gone by this point. initModelPicker() (called
  // below, once) populates + shows it in hosted mode and leaves it hidden in
  // local mode -- identical behavior to v1, just newly reachable for v2
  // (closes the hosted-topbar-model-picker gap for the chat view).
  const modelSelect = document.createElement("select");
  modelSelect.id = "modelSelect";
  modelSelect.className = "select-model nav-dropdown";
  modelSelect.hidden = true;

  // #autonomyChip relocates a SECOND time here (M10 T1) -- M9 T3 originally
  // moved it into the chat HEADER (.chat-panel-actions); that mount is now
  // superseded and removed (bootShellV2(), shell-v2.js, no longer touches
  // it), so this is the chip's only home, keeping the "exactly one instance"
  // invariant. Same real element/binding (wireChatPanel()'s bind(), already
  // ran by the time this function executes -- see its own call-order
  // comment), just a further reparent + a swapped click behavior:
  // wireChatPanel() attached _autonomyChipDefaultClick (v1's -- and v2's
  // until now -- "click just refreshes" behavior); removed here BY REFERENCE
  // and replaced with the real interactive toggle (_onAutonomyToggleClick,
  // above), so only ONE listener ever runs per click (no double-POST/GET
  // race from stacking both).
  const autonomyChip = document.getElementById("autonomyChip");
  if (autonomyChip) {
    autonomyChip.removeEventListener("click", _autonomyChipDefaultClick);
    autonomyChip.addEventListener("click", _onAutonomyToggleClick);
  }

  // Static hint (mock: "@ files · / commands · Enter to send") -- built here,
  // not baked into chat-markup.js, for the same v1-byte-identical reason as
  // the rest of this function. Fully static text, no interpolation -- no
  // esc()/escAttr() needed (mirrors #v2ActBtn's own static innerHTML, M9 T3).
  const hint = document.createElement("span");
  hint.className = "hint";
  hint.innerHTML = `<span class="mono">@</span> files &middot; <span class="mono">/</span> commands &middot; <span class="mono">Enter</span> to send`;

  // New paperclip -- inserts "@" at the cursor and focuses, triggering the
  // REAL autocomplete.js flow via a genuine "input" event. Not byte-identical
  // to autocomplete.js's own acSelect(), which dispatches `new Event("input")`
  // WITHOUT `bubbles: true` (autocomplete.js line ~165) -- but the "input"
  // listener that matters (bindEvents(), directly on #promptInput, not
  // delegated) fires regardless of `bubbles`, so the difference is inert
  // here. Not a fake upload affordance -- the existing #btnAttach file-upload
  // button is hidden for v2 below, superseded by this button visually; its
  // real file-upload behavior is untouched and still v1-reachable.
  //
  // HOSTED: the paperclip opens the real file picker instead (a paperclip that
  // picks files is its universally expected meaning, and hosted attachments now
  // work for Word/Markdown); "@" mention stays available by typing "@" (the
  // composer hint documents it). Local keeps the "@"-insert behavior unchanged.
  const attachHosted = getAuthMode() === "hosted";
  const attachBtn = document.createElement("button");
  attachBtn.type = "button";
  attachBtn.id = "v2Attach";
  attachBtn.className = "attach";
  attachBtn.title = attachHosted
    ? "Attach a Word or Markdown file, or paste text"
    : "Mention a file (@)";
  attachBtn.innerHTML = `<span class="ic ic-paperclip"></span>`;
  // A native <button> shifts focus to itself on mousedown by default, which
  // would blur #promptInput BEFORE the click handler below runs -- that blur
  // arms autocomplete.js's hideAutocomplete() 150ms setTimeout(acHide), so
  // the popup this same click reopens would self-close ~150ms later with no
  // further user input. Same fix as autocomplete.js's own `.at-item` rows.
  attachBtn.addEventListener("mousedown", (e) => e.preventDefault());
  attachBtn.addEventListener("click", () => {
    if (attachHosted) {
      document.getElementById("fileAttachInput")?.click();
      return;
    }
    const input = document.getElementById("promptInput");
    if (!input) return;
    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? input.value.length;
    input.value = input.value.slice(0, start) + "@" + input.value.slice(end);
    input.focus();
    input.selectionStart = input.selectionEnd = start + 1;
    input.dispatchEvent(new Event("input", { bubbles: true }));
  });

  // Relocate the real send button (same id/handler) into the new row;
  // restyled round+gradient via #appV2-scoped CSS (shell-v2.css).
  btnSend.classList.add("send");

  const cmpBtns = document.createElement("span");
  cmpBtns.className = "cmp-btns";
  cmpBtns.appendChild(attachBtn);
  cmpBtns.appendChild(btnSend);

  const bot = document.createElement("div");
  bot.className = "composer-bot";
  bot.appendChild(btnModel);
  bot.appendChild(modelSelect);
  // Between #v2CmpModel and the hint, per the mock layout. HOSTED: skip the append —
  // the toggle would 404 (autopilot is not a hosted concept), and un-relocated the chip
  // stays inside the v2-hidden .composer-footer-row. Skipping (vs visibility:hidden)
  // means flex `gap` leaves no phantom slot in the row.
  if (autonomyChip && getAuthMode() !== "hosted") bot.appendChild(autonomyChip);
  bot.appendChild(hint);
  bot.appendChild(cmpBtns);
  composer.appendChild(bot);

  // Relocate #backendMenu alongside its (now-relocated) trigger button so it
  // still opens visually anchored near it -- was a sibling of
  // .chat-panel-actions under .chat-panel-header (top:100%); now a sibling
  // inside the composer card, repositioned (shell-v2.css) to open UPWARD
  // since the button now lives at the bottom of the chat view.
  const menu = document.getElementById("backendMenu");
  if (menu) composer.appendChild(menu);

  // v2-only: disable the chip + explain why when the tenant's .cpq-agent.md
  // directive owns autonomy -- a write would be silently overridden (the
  // same gate the backend enforces with 409, see server/_routes_agent.py's
  // set_autonomy()). Layered ON TOP of wireChatPanel()'s shared label/tone/
  // dot render (which has no notion of "source") via a SECOND, independent
  // effect(), not a replacement for it -- both re-run on every autonomyAtom
  // change. Registration order matters here and is safe by construction:
  // this effect() is registered AFTER wireChatPanel()'s bind() (this
  // function always runs later in initV2Shared(), see its own call site
  // comment), and store.js's effects fire in registration order on each
  // update (Set iteration is insertion-order) -- so this always re-asserts
  // AFTER the shared render, meaning its title override in the pinned
  // branch always wins, and the un-pinned branch deliberately leaves title
  // untouched (the shared render's fresh tooltip already stands).
  if (autonomyChip) {
    effect(() => {
      const s = autonomyAtom.get();
      const pinned = s.status === "ok" && s.autonomy_source === "tenant_md";
      autonomyChip.disabled = pinned;

      // M11 T2 -- Kimi-style "Auto" toggle visual, layered on top of
      // wireChatPanel()'s shared tone/label/dot render exactly like the
      // pinned-disable logic above (same effect, same registration-order
      // argument: this whole function only ever runs for v2 --
      // _buildComposerV2() is v2-only -- so v1 keeps the shared
      // "Local-only"/"Autopilot -> host" label + tone-calm/armed/neutral
      // classes verbatim). The visible label collapses to a constant short
      // "Auto" (state now lives in the title, not the label); a v2-only
      // .v2-auto-on/.v2-auto-off marker class (shell-v2.css keys the
      // brand-vs-muted restyle off THESE, not off tone-armed/tone-calm
      // directly) keeps the safety-tone semantic decoupled from the
      // toggle's new visual language. "on" only for a genuinely resolved
      // autopilot state -- matches describeAutonomy()'s own fail-safe
      // stance (loading/error/local_only all read as off). The dot span
      // gets the "ic-cpu" class added on top of its own shared
      // "autonomy-dot dot-X" classes so it renders via the same generic
      // icon-mask mechanism (icons.css) every other icon in the app uses.
      const vm = describeAutonomy(s);
      const armed = vm.tone === "armed";
      autonomyChip.classList.toggle("v2-auto-on", armed);
      autonomyChip.classList.toggle("v2-auto-off", !armed);
      const dot = autonomyChip.querySelector(".autonomy-dot");
      if (dot) dot.classList.add("ic-cpu");
      const label = autonomyChip.querySelector(".autonomy-label");
      if (label) label.textContent = "Auto";
      if (s.status === "ok") {
        const tenantLabel = s.tenant_host || s.tenant || "the live tenant";
        autonomyChip.title = armed
          ? `Auto-mode (autopilot): the agent pushes changes without per-change approval. Currently: autopilot → ${tenantLabel} — pushing without approval.`
          : `Auto-mode (autopilot): the agent pushes changes without per-change approval. Currently: local-only — every push needs your approval.`;
      }
      if (pinned) autonomyChip.title = "Set by .cpq-agent.md";
    });
  }

  initModelPicker();
}

// ---------------------------------------------------------------------------
// Editor panel (center zone)
// ---------------------------------------------------------------------------

function _populateEditorPanel() {
  const editorPanel = getPanelContainer('editor');
  if (!editorPanel) return;

  editorPanel.innerHTML = `
    <div class="editor-panel">
      <div class="file-viewer" id="fileViewer"></div>
    </div>
  `;

  // Cache the new fileViewer ref
  refs.fileViewer = document.getElementById('fileViewer');
}

// ---------------------------------------------------------------------------
// Console panel (bottom zone)
// ---------------------------------------------------------------------------

// Exported (M12 T3): reused verbatim by shell-v2.js's showViewV2() "console"
// branch to build v2's Console view (v1-panel id-reuse into #panel-console
// -- see that branch's own comment). Still main.js-module-private in spirit
// (leading underscore kept), just legitimately cross-module-reused now.
export function _populateConsolePanel() {
  const consolePanel = getPanelContainer('console');
  if (!consolePanel) return;

  // Test-only invariant counter (M12 fix round): counts every time this
  // function's body actually executes, regardless of which caller reached it
  // (v1's init(), v2's showViewV2() "console" branch, or any future path).
  // Moved here (from a `_consolePopulateCount++` at shell-v2.js's guard
  // call-site) because incrementing at the call site only proved the
  // `!_consoleViewRendered` guard was checked, not that the populate logic
  // itself never runs twice via some other path. Read via
  // window.__test_getConsolePopulateCount() (shell-v2.js). Uses `window`
  // (not an ES import) so main.js and shell-v2.js don't form a static
  // import cycle -- same bridge pattern as window.renderStage above (this
  // file's top).
  window.__consolePopulateCount = (window.__consolePopulateCount || 0) + 1;

  // v2: the Console is a NAV_MODEL view (Operate group), not a collapsible
  // bottom zone -- there's nothing for a "Close panel" button to do there
  // (the user just clicks a different nav item), so it's dropped from the
  // DOM entirely rather than left wired to the v1-only collapseZone('bottom')
  // below (which would be a silently-inert dead button in v2: no visual
  // effect, plus a stray write to v1's own saved layout config in
  // localStorage). Gated on the same #appV2-existence check
  // wireBackendPicker()'s renderMenu() already uses elsewhere in this file
  // for v1-vs-v2 branching inside otherwise-shared code.
  const isV2 = !!document.getElementById("appV2");

  consolePanel.innerHTML = `
    <div class="console-panel">
      <div class="console-header">
        <span class="console-title">Console</span>
        <div class="console-actions">
          ${isV2 ? "" : `<button class="btn-icon" id="btnCloseBottom" title="Close panel">
            <i class="ic ic-x"></i>
          </button>`}
        </div>
      </div>
      <div class="console-tabs">
        <button class="console-tab active" data-tab="dev-console">Dev Console</button>
        <button class="console-tab" data-tab="pipeline-trace">Pipeline Trace</button>
      </div>
      <div class="console-body" id="devConsoleOutput" data-tab="dev-console"></div>
      <div class="console-body" id="pipelineTraceOutput" data-tab="pipeline-trace" hidden></div>
    </div>
  `;

  // Mount the global trace panel into the Dev Console tab
  const devConsoleContainer = document.getElementById('devConsoleOutput');
  if (devConsoleContainer) {
    initGlobalTracePanel(devConsoleContainer);
  }

  // Wire tab switching
  for (const tab of consolePanel.querySelectorAll('.console-tab')) {
    tab.addEventListener('click', () => {
      consolePanel.querySelectorAll('.console-tab').forEach(t => t.classList.remove('active'));
      tab.classList.add('active');
      consolePanel.querySelectorAll('.console-body').forEach(b => b.hidden = b.dataset.tab !== tab.dataset.tab);
    });
  }

  document.getElementById('btnCloseBottom')?.addEventListener('click', () => collapseZone('bottom'));
}

// ---------------------------------------------------------------------------
// Quote Frame panel (bottom zone)
// ---------------------------------------------------------------------------

// Exported (M12 T2): reused verbatim by shell-v2.js's showViewV2() "quote"
// branch to build v2's Quote view (v1-panel id-reuse into
// #panel-quote-frame -- see that branch's own comment). No v1-vs-v2 branch
// needed inside this one (unlike _populateConsolePanel() above): the
// pop-out/close buttons below have no zone-system dependency to begin with.
export function _populateQuoteFramePanel() {
  const quotePanel = getPanelContainer('quote-frame');
  if (!quotePanel) return;

  quotePanel.innerHTML = `
    <div class="quote-frame-panel">
      <div class="quote-frame-header">
        <span class="quote-frame-title" id="quoteFrameTitle">Active Quote Frame</span>
        <div class="quote-frame-actions">
          <button class="btn-icon" id="btnQuotePopout" title="Open in new window">
            <i class="ic ic-external-link"></i>
          </button>
          <button class="btn-icon" id="btnQuoteClose" title="Close quote">
            <i class="ic ic-x"></i>
          </button>
        </div>
      </div>
      <div class="quote-frame-body" id="quoteFrameBody">
        <div class="quote-frame-placeholder">No quote loaded. Use chat to open a quote.</div>
      </div>
    </div>
  `;

  // Pop-out: open quote in a new browser window at 70% screen size
  document.getElementById('btnQuotePopout')?.addEventListener('click', () => {
    const iframe = document.getElementById('quoteFrameIframe');
    if (!iframe || !iframe.src) return;
    const w = Math.round(screen.width * 0.7);
    const h = Math.round(screen.height * 0.7);
    const left = Math.round((screen.width - w) / 2);
    const top = Math.round((screen.height - h) / 2);
    window.open(iframe.src, '_blank', `width=${w},height=${h},left=${left},top=${top}`);
  });

  // Close: send WS message and reset panel to placeholder
  document.getElementById('btnQuoteClose')?.addEventListener('click', () => {
    if (state.qteQuoteId) {
      wsSend({ type: 'quote_close', quote_id: state.qteQuoteId });
    }
    resetQuotePanel();
  });
}

// ---------------------------------------------------------------------------
// Settings menu (dropdown from gear icon)
// ---------------------------------------------------------------------------


// ---------------------------------------------------------------------------
// Timeline panel (left zone, hidden by default)
// ---------------------------------------------------------------------------

function _populateTimelinePanel() {
  const panel = getPanelContainer('timeline');
  if (!panel) return;

  panel.innerHTML = `
    <div class="timeline-panel-inner">
      <div class="tl-head">
        <span>Timeline</span>
        <button id="tlCheckpoint" title="Save a named checkpoint"><i class="ic ic-sm ic-flag"></i> Checkpoint</button>
      </div>
      <div id="timelineBody"></div>
    </div>
  `;

  // Wire Checkpoint via event DELEGATION (once), not a direct listener: the
  // timeline panel's content is re-created by the panel framework after init,
  // so a listener bound to the init-time #tlCheckpoint never fires on the
  // visible button. Delegating from document survives any re-render. Uses the
  // app's <dialog> name prompt (confirmName) instead of the blocking prompt().
  if (!_tlCheckpointWired) {
    _tlCheckpointWired = true;
    document.addEventListener('click', async (e) => {
      const t = e.target;
      if (!t || !t.closest || !t.closest('#tlCheckpoint')) return;
      // Defensive: the checkpoint tool rides the per-tenant desktop route, which is gated
      // in hosted mode (this v1 panel isn't rendered there today, but a future mount must
      // not silently 404).
      if (getAuthMode() === "hosted") {
        notify("Checkpoints are not available in the hosted app yet.", "info");
        return;
      }
      const label = await confirmName(
        'Save checkpoint',
        'Name this checkpoint so you can roll back to it later (e.g. "before-excise-refactor").',
        '',
      );
      if (!label || !label.trim()) return;
      try {
        await api.checkpointCreate(label.trim());
        await refreshTimeline();
      } catch (err) {
        console.error('checkpoint_create failed', err);
      }
    });
  }
}
let _tlCheckpointWired = false;

// ---------------------------------------------------------------------------
// Run Traces panel (left zone, hidden by default)
// ---------------------------------------------------------------------------

function _populateTracesPanel() {
  const panel = getPanelContainer('traces');
  if (!panel) return;

  panel.innerHTML = `
    <div class="traces-head">
      <span class="traces-head-title">Traces</span>
      <button class="traces-refresh-btn" id="btnRefreshTraces" title="Refresh"><i class="ic ic-sm ic-refresh-cw"></i></button>
    </div>
    <div class="traces-body" id="tracesBody"></div>
  `;
  // Drag header registered by the makePanelHeaderDraggable(...) loop in init().
}

// ---------------------------------------------------------------------------
// Promotion panel (left zone, hidden by default) — hosted release promotion
// ---------------------------------------------------------------------------

function _populatePromotionPanel() {
  const panel = getPanelContainer('promotion');
  if (!panel) return;

  panel.innerHTML = `
    <div class="promotion-panel-inner">
      <div class="prom-head">
        <span>Promotion</span>
        <button id="promRefresh" title="Refresh releases"><i class="ic ic-sm ic-refresh-cw"></i></button>
      </div>
      <div id="promotionBody"></div>
    </div>
  `;
  // Click/change/input handlers wired by initPromotionPanel(); drag header by the
  // makePanelHeaderDraggable(...) loop in init().
}

// ---------------------------------------------------------------------------
// Errors panel (left zone, hidden by default)
// ---------------------------------------------------------------------------

function _populateErrorsPanel() {
  const panel = getPanelContainer('errors');
  if (!panel) return;

  panel.innerHTML = `
    <div class="errors-head">
      <span class="errors-head-title">Errors</span>
      <button class="errors-refresh-btn" id="btnRefreshErrors" title="Refresh"><i class="ic ic-sm ic-refresh-cw"></i></button>
    </div>
    <div class="errors-body" id="errorsBody"></div>
  `;
  // Drag header registered by the makePanelHeaderDraggable(...) loop in init().
}

// ---------------------------------------------------------------------------
// Learning panel (left zone, hidden by default)
// ---------------------------------------------------------------------------

function _populateLearningPanel() {
  const panel = getPanelContainer('learning');
  if (!panel) return;

  panel.innerHTML = `
    <div class="learning-head">
      <span class="learning-head-title">Learning</span>
      <button class="lrn-refresh-btn" id="btnRefreshLearning" title="Refresh"><i class="ic ic-sm ic-refresh-cw"></i></button>
    </div>
    <div class="learning-body" id="learningBody"></div>
  `;
  // Drag header registered by the makePanelHeaderDraggable(...) loop in init().
}

// ---------------------------------------------------------------------------
// Pipelines panel (center zone, hidden by default)
// ---------------------------------------------------------------------------

function _populatePipelinesPanel() {
  const panel = getPanelContainer('pipelines');
  if (!panel) return;
  panel.innerHTML = `
    <div class="pipeline-header pl-head"><span>Pipelines</span><button class="pl-close" title="Close (back to editor)" aria-label="Close Pipelines">&times;</button></div>
    <div class="pl-board-shell"></div>
  `;
  panel.querySelector('.pl-close').addEventListener('click', () => {
    hidePanel('pipelines');
    showPanel('editor');
  });
  makePanelHeaderDraggable('pipelines');
}

// ---------------------------------------------------------------------------
// Init
// ---------------------------------------------------------------------------

// Curated shared wiring for the v2 shell: runs the SAME functions v1's init()
// uses for chat send/receive + the agent dock, against the v2 DOM that
// bootShellV2() already built (chatPanelMarkup() mounted into #v2ChatMount,
// #agentDock reparented into #v2ActivityBody -- M9 T3's Activity popover, not
// a context column). Deliberately narrower than v1's init() body -- v2
// doesn't have the zone/panel/activity-bar/rail system, so none of that is
// wired here; only chat + the ws/dock plumbing it depends on.

// Idle-fire the Database table-list prewarm off the boot-critical path (both
// modes). requestIdleCallback yields to the boot burst so the cold ~3.7s
// /api/db/tables round trip never blocks boot or competes with the boot
// fetches; the timeout caps the wait so a busy page still warms within seconds;
// setTimeout is the fallback where requestIdleCallback is unavailable.
// Fire-and-forget -- prewarmDatabaseTables() no-ops if already warmed, and
// routes per-connection (hosted) or single-operator (local) via api.js's
// _dbBase().
function _idlePrewarmDatabase() {
  const warm = () => prewarmDatabaseTables();
  if (window.requestIdleCallback) window.requestIdleCallback(warm, { timeout: 4000 });
  else setTimeout(warm, 1500);
}

async function initV2Shared() {
  initTheme();        // apply the saved/OS theme; v1 parity (init() calls this before cacheRefs() too)
  cacheRefs();       // idempotent (state.js); must run after the v2 DOM exists
  // v2 errors nav follows the same admin gate as the v1 errors panel: hosted
  // mode exposes only the global/admin error feed, so hide the nav entry for
  // a non-admin (always visible in local mode). See applyErrorsNavVisibilityV2()
  // above for the full rationale.
  applyErrorsNavVisibilityV2();
  // Same admin gate, applied to the Learning nav item — see applyLearningNavVisibilityV2().
  applyLearningNavVisibilityV2();
  // 1.0 tier split: Promotion is plan-gated (Max) — see applyPromotionNavVisibilityV2().
  applyPromotionNavVisibilityV2();
  // Hosted-mode nav coherence: hide the desktop-only Sync view — see applyHostedNavVisibilityV2().
  applyHostedNavVisibilityV2();
  // v2's sidebar tenant card reuses v1's #tenantSwitcher/#tenantDropdown/#tenantList/
  // #btnAddTenant ids (shell-v2.js's _sidebarTopHtml(), M9 T2 -- moved out of the topbar
  // into .v2-side), so the same wiring works unmodified -- called explicitly here (right
  // after cacheRefs() populates refs.tenantSwitcher etc. from the v2 DOM) rather than
  // relying solely on bindEvents() below to reach it, so this doesn't silently break if
  // bindEvents() is ever reordered. Idempotent-once, so bindEvents() calling it again
  // below is a safe no-op.
  wireTenantSwitcher();
  // Hosted gates below: the single-operator boot loads (/api/sync/status,
  // /api/conversations, /api/tenants) are 404-gated legacy surface in hosted
  // mode (IdentityAuthMiddleware), so skip them instead of firing calls that
  // can only fail -- the per-connection equivalents load through their own
  // hosted-aware paths (auth.js boot + api.js re-points).
  const hosted = getAuthMode() === "hosted";
  // Populate state.syncStatusMap so getSyncStatus()/syncBadgeHtml() (code-view.js) can render sync
  // badges in v2 -- v1 parity for the data load, not the DOM. Fire-and-forget, like the WS-driven
  // reloads of this same function elsewhere in this file. Safe pre-DOM: renderFileTree() (file-tree.js)
  // no-ops when refs.fileTree is null (v2 never creates #fileTree), and setPanelLoading()/
  // renderWorkspaceTabs() likewise no-op when their v1-only panel/tab-bar DOM is absent.
  if (!hosted) loadSyncStatuses();
  // Idle-prewarm the Database table-name list OFF the boot-critical path. Local
  // mode fires here (its tenant is already active); hosted fires from the
  // connection-ready branch below, once a connection is selected (api.dbTables()
  // then routes per-connection via _dbBase()). See _idlePrewarmDatabase().
  if (!hosted) _idlePrewarmDatabase();
  wireChatPanel();   // autonomy chip, initAgentDock({onCancel: cancelInFlight}), wireBackendPicker(), history dropdown
  _buildComposerV2(); // composer card -- must run after wireChatPanel() (see that function's own comment)
  // v1's init() wires initNameConfirmDialog()/initUsageDialog()/initTemplatePickerDialog()
  // in its OWN (post-early-return) branch, so the v2 boot path above needs its own call to
  // wire these three sibling dialogs: initNameConfirmDialog() (#nameConfirmOk/#nameConfirmCancel
  // -- confirmName(), used when saving a new script/calculation under a
  // suggested name), initUsageDialog() (#usageOk/#usageCancel -- the
  // "More info…"/push-to-cloud artifact-details dialog), and
  // initTemplatePickerDialog() (#templatePickerOk/#templatePickerCancel --
  // the doc-template scaffold picker). All three dialogs are body-level
  // siblings of #app/#appV2 present in the base index.html
  // regardless of shell, so calling their init here is safe/idempotent.
  // initFormulaPopup() (pipeline.js) is
  // NOT folded in here -- it wires the Execution Order panel's formula
  // popup, out of scope here (v2's Execution order view has its own
  // separate rendering path, pipeline.js's renderExecOrderInto()).
  initNameConfirmDialog();
  initUsageDialog();
  initTemplatePickerDialog();
  lockInput();       // mirrors v1: locked transiently while the rest of boot wiring below runs
  setWsHandler(handleWsMessage);
  bindEvents();      // composer send/keydown/attach/paste + welcome chips (verified null-safe for the v2 DOM)
  // v1's init() only calls initSearch() in its OWN (post-early-return)
  // branch, so v2 never wired the search dialog's interactivity (typing,
  // toggles, scope tabs, close button, Escape), even though
  // bindEvents() above (already called for v2) wires the Cmd+Shift+F shortcut
  // AND the new #v2SearchBtn topbar button (shell-v2.js) both already call the
  // shared openSearch() -- so the dialog opened, but typing a query did
  // nothing. refs.searchDialog/searchInput/etc. are all body-level siblings of
  // #app/#appV2 (index.html), present regardless of shell, so this is
  // safe/idempotent to call from the v2 boot path the same way v1's does.
  // v1 order preserved (bindEvents() then initSearch(), matching init()).
  initSearch();
  // M12 fix round (MINOR-5): v1's init() wires 'session-switch' ->
  // rerenderTraces() (see the identical try/catch further down this file, in
  // the v1-only branch of init()) so the Console panel's global trace list
  // re-renders from the newly-active session's own state.cfgTraces buffer
  // (a per-session-proxied field, state.js's PER_SESSION_FIELDS -- switching
  // sessions swaps the proxy target but notifies nothing, state.js's own
  // "regime 2" comment). v2's init() branch returns early BEFORE that
  // listener is ever wired (see the early `return` a few dozen lines up this
  // file), so a v2 Console left mounted across a conversation switch kept
  // showing the OLD session's stale trace buffer forever. rerenderTraces()
  // itself (traces.js) no-ops when the Console hasn't been visited yet (no
  // #cfg-trace-panel-global in the DOM), so wiring this unconditionally here
  // is safe regardless of whether the Console view has ever been opened.
  window.addEventListener('session-switch', () => {
    try { rerenderTraces(); } catch (e) { console.warn('traces rerender on switch failed', e); }
  });
  if (!hosted) await loadConversations();
  // Hosted: fire-and-forget the same load — the chat header derives the
  // active conversation's title from state.savedConversations, and the nav
  // count chip benefits too. Never awaited (a slow list must not block boot);
  // loadConversations() already swallows fetch errors.
  else loadConversations();
  // Identity for the v2 sidebar conn-card (#v2TenantLabel lives in the card,
  // _sidebarTopHtml()). Hosted: the active connection's label (the tenant
  // registry is a gated legacy surface). Local: mirrors v1's loadTenants().
  let didSwitch = false;
  const tenantLabelEl = document.getElementById("v2TenantLabel");
  if (hosted) {
    // Render the conn-card immediately from connectionMeta (label + env badge + SAP host);
    // start the dot at "connecting" and let the SAP health probe below resolve it async so a
    // slow probe never blocks boot.
    state.connHealth = "connecting";
    const meta = await api.connectionMeta().catch(() => null);
    if (meta) updateHostedConnCardMeta(meta);
    else if (tenantLabelEl) tenantLabelEl.textContent = "Not connected";
    // Replay the resumed conversation's persisted messages so a page reload
    // doesn't present a blank chat (v1 parity: init()'s own hosted replay).
    // Unawaited on purpose (a slow fetch must not block boot), so it races the
    // run socket's resume replay below (connectWs) — preserveLive makes the two
    // compose in either completion order instead of clobbering the live turn.
    const cid = getActiveConnection();
    const coid = getActiveConversation();
    if (cid && coid) {
      api.connectionMessages(cid, coid)
        .then((d) => { if (d && d.messages && d.messages.length) loadConversationMessages(d.messages, { preserveLive: true }); })
        .catch(() => {});
    }
    if (cid) {
      // Warm THIS connection's Database table list off the boot path -- same
      // idle-fire as local, but api.dbTables() now routes per-connection
      // (/api/connections/{cid}/db/tables) via _dbBase(). Fire-and-forget; a
      // connection switch clears the cache + invalidates this via _fetchToken.
      _idlePrewarmDatabase();
      api.connectionHealth(cid)
        .then((h) => { if (getActiveConnection() === cid) state.connHealth = h.ok ? "ok" : "off"; })
        .catch(() => { state.connHealth = "off"; });
      // Per-connection file tree: feeds the composer's @-file autocomplete
      // (autocomplete.js walks state.fileTree) and the file-tree surfaces.
      // v1's init() already does this for hosted; the v2 boot path missed it,
      // which left "@" permanently on "No matching scripts". Fire-and-forget:
      // a slow/failed fetch degrades to the empty suggestion state, never
      // blocks boot.
      loadFileTree();
    }
  } else {
    // Sidebar conn-card dot: CONNECTING for the boot-time initial probe too,
    // not just an explicit later switch/login -- otherwise the dot sits
    // dishonestly grey (its initial baked-in markup state) while this GET is
    // still in flight, indistinguishable from "confirmed no tenant". Resolved
    // here UNLESS loadTenants() itself auto-switched (didSwitch true): that
    // path runs through tenants.js's switchTenant(), which already owns (and
    // will correctly resolve, including the deferred new-tenant/WS case) its
    // own connecting lifecycle -- resolving it again here would race it.
    state.tenantConnecting = true;
    didSwitch = await loadTenants().catch(() => false);
    if (!didSwitch) state.tenantConnecting = false;
    if (tenantLabelEl) {
      tenantLabelEl.textContent = state.tenant ? tenantDisplayLabel(state.tenant) : "Not connected";
      updateTenantCardMeta(state.tenant); // M9 T2: sidebar card's env/host, additive
    }
  }
  refreshAutonomy(); // hosted-aware: resolves without a fetch in hosted mode (agent-store.js)
  // (Hosted model picker: #modelSelect lives in the v2 COMPOSER and
  // initModelPicker() is already called by _buildComposerV2() above -- a second
  // call here would double-bind the select's change listener.)
  connectWs();
  // Unlock unless loadTenants() just auto-switched to a saved tenant (mirrors
  // v1's own `if (!didSwitch) unlockInput()` at the end of its init()): a
  // switch that hit the "new tenant" branch (switchTenant(), tenants.js)
  // leaves the composer intentionally locked until the WS "initial_sync_done"
  // message unlocks it -- unlocking unconditionally here would race that. A
  // switch on an already-synced tenant unlocks itself from inside
  // switchTenant(), so skipping this call in that case is a no-op, not a
  // regression. The common case (a tenant is already active, no switch
  // happens) still unlocks unconditionally here, same as before.
  if (!didSwitch) {
    unlockInput();
  }
}

async function init() {
  if (getShell() === "v2") {
    const { bootShellV2 } = await import("./shell-v2.js");
    await bootShellV2();
    await initV2Shared();
    // Load-bearing: shell-v2.js's Traces/Errors views reuse the v1 ids
    // (#panel-traces/#panel-errors) -- this return must stay BEFORE
    // _populateTracesPanel()/_populateErrorsPanel() below ever run.
    return; // v2 owns the DOM; skip all v1 layout/panel/rail/activity wiring
  }
  initTheme();
  cacheRefs();
  loadPinnedArtifacts();
  bindArtifactHovers();
  initLayout();
  const layoutConfig = getLayoutConfig();
  initPanels(layoutConfig);
  _populateExplorerPanel();
  _populateExecOrderPanel();
  _populateKnowledgePanel();
  _populateDbInspectorPanel();
  // diff panel template is built lazily by showDiffPanel() in diff-panel.js
  populateSyncPanel();
  _populateEditorPanel();
  _populateChatPanel();
  _populateConsolePanel();
  _populateQuoteFramePanel();
  _populateTimelinePanel();
  _populateTracesPanel();
  _populatePromotionPanel();
  _populateErrorsPanel();
  _populateLearningPanel();
  _populatePipelinesPanel();

  // Apply errors-panel visibility immediately after the shell is built.
  // In hosted mode _isAdmin is already populated from /api/me (both resume and login paths
  // fetch it before init() runs). In local mode getAuthMode() !== "hosted" so always visible.
  applyErrorsPanelVisibility();
  // Same gate, same timing, for the Learning panel — see applyLearningPanelVisibility().
  applyLearningPanelVisibility();
  // 1.0 tier split: Promotion is plan-gated (Max) — see applyPromotionPanelVisibility().
  applyPromotionPanelVisibility();

  // Reactive spine: wire sync + exec-order consumers to their signals now that
  // the panels' DOM exists. After this, sync state and exec-order stay live.
  initSyncLiveBindings();
  initExecOrderLiveBinding();

  // S1: per-session panels (exec-order, Dev Console traces, editor) are NOT
  // signal-backed — switching the active session swaps state.* via the proxy but
  // notifies nothing, so re-render them here. renderStage() fires 'session-switch'
  // after the proxy has already swapped to the new session's sub-state.
  window.addEventListener('session-switch', () => {
    try { renderPipeline(); } catch (e) { console.warn('exec-order rerender on switch failed', e); }
    try { rerenderTraces(); } catch (e) { console.warn('traces rerender on switch failed', e); }
    try { switchWorkspaceTab(currentSession().activeWorkspaceTab || null); } catch (e) { console.warn('editor resync on switch failed', e); }
  });

  // Resolve the agent's autonomy so the composer chip shows it on first paint,
  // and load the tool catalog so the dock renders legible tool verbs.
  refreshAutonomy();
  loadToolCatalog();
  // Hosted: populate the header model picker from the operator's catalog; local: hide it
  // (the backend is operator-configured, there is nothing to pick).
  initModelPicker();

  // Editor "Explain/Fix/Send selection" actions → prefill the composer.
  window.addEventListener('send-selection-to-agent', (e) => prefillComposerFromSelection(e.detail));
  // Promotion run "Ask agent" button on failed/blocked rows → prefill the composer (manual send).
  window.addEventListener('promotion-ask-agent', (e) => prefillComposerFromPromotionFailure(e.detail));
  // Promotion "Fix N with agent" footer button → consolidated fix prompt + auto-send.
  window.addEventListener('promotion-fix-agent', (e) => fixWithAgent(e.detail));

  _syncActivityBar();

  // Prevent panel drags from inserting text into the Monaco editor.
  // When dragging a panel tab over the editor, the browser would otherwise
  // treat it as a text/plain drop and insert the panel ID into the editor.
  const editorPanel = document.getElementById('panel-editor');
  if (editorPanel) {
    editorPanel.addEventListener('dragover', (e) => {
      if (e.dataTransfer.types.includes('application/x-agentcpq-panel')) {
        e.preventDefault();
        e.stopPropagation();
      }
    });
    editorPanel.addEventListener('drop', (e) => {
      if (e.dataTransfer.types.includes('application/x-agentcpq-panel')) {
        e.preventDefault();
        e.stopPropagation();
      }
    });
  }

  // Make panel headers draggable for drag-and-drop between zones
  for (const panelId of ['explorer', 'exec-order', 'knowledge', 'db-inspector', 'sync-status', 'timeline', 'traces', 'promotion', 'errors', 'learning', 'diff', 'pipelines', 'chat', 'console', 'quote-frame']) {
    makePanelHeaderDraggable(panelId);
  }
  lockInput();
  setWsHandler(handleWsMessage);
  bindEvents();
  initSearch();
  initSidebarSearch();
  initScaffold();
  initPreviewPanel();
  initKnowledgePanel();
  initNameConfirmDialog();
  initUsageDialog();
  initTemplatePickerDialog();
  initFormulaPopup();

  // Auto-load data for restored panels (layout persistence may restore
  // exec-order or knowledge as the active left panel — their data-loading
  // functions only fire on click, so trigger them here on init).
  const restoredLeft = getActivePanel('left');
  if (restoredLeft === 'exec-order') {
    loadPipeline();
  } else if (restoredLeft === 'knowledge') {
    const kbEl = document.getElementById('panel-knowledge');
    if (kbEl) kbEl._kbInited = true;  // listeners already wired above
    onKnowledgeActivated();
  } else if (restoredLeft === 'db-inspector') {
    const dbEl = document.getElementById('panel-db-inspector');
    if (dbEl) { dbEl._dbInited = true; initDbInspector(); }
  } else if (restoredLeft === 'sync-status') {
    renderSyncPanel();
  } else if (restoredLeft === 'timeline') {
    const tlEl = document.getElementById('panel-timeline');
    if (tlEl) { tlEl._tlInited = true; initTimelinePanel(); }
    onTimelineActivated();
  } else if (restoredLeft === 'traces') {
    const trEl = document.getElementById('panel-traces');
    if (trEl) { trEl._tracesInited = true; initTracesPanel(); }
    onTracesActivated();
  } else if (restoredLeft === 'promotion') {
    const prEl = document.getElementById('panel-promotion');
    if (prEl) { prEl._promInited = true; initPromotionPanel(); }
    onPromotionActivated();
  } else if (restoredLeft === 'errors') {
    const erEl = document.getElementById('panel-errors');
    if (erEl) { erEl._errorsInited = true; initErrorsPanel(); }
    onErrorsActivated();
  } else if (restoredLeft === 'learning') {
    const lrnEl = document.getElementById('panel-learning');
    if (lrnEl) { lrnEl._learningInited = true; initLearningPanel(); }
    onLearningActivated();
  }

  // Pipelines is a center-zone panel — check center instead of left
  const restoredCenter = getActivePanel('center');
  if (restoredCenter === 'pipelines') {
    const plEl = document.getElementById('panel-pipelines');
    if (plEl) { plEl._plInited = true; initPipelinesPanel(); }
    onPipelinesActivated();
  }

  // Activate Pipelines board whenever it becomes the active center panel
  // (e.g. toggled on from the View menu, or dragged into center zone).
  window.addEventListener('layout-changed', () => {
    if (getActivePanel('center') !== 'pipelines') return;
    const plEl = document.getElementById('panel-pipelines');
    if (!plEl || plEl.hidden) return;
    if (!plEl._plInited) {
      initPipelinesPanel();
      plEl._plInited = true;
    }
    onPipelinesActivated();
  });

  // Load conversation history
  renderConversations();
  loadConversations();

  // In hosted mode /api/tenants is gated (legacy single-operator surface) and 401s — don't let it
  // abort init() before connectWs(). The per-connection surface (Phase 4) replaces it for the web.
  const didSwitch = await loadTenants().catch(() => false);
  // Reload pinned artifacts now that state.tenant is set (key is tenant-scoped).
  // The early loadPinnedArtifacts() call above uses "default" as tenant; this
  // corrects it once the active tenant name is known.
  loadPinnedArtifacts();
  checkHealth().then(renderConnStatus);
  updateNavAvatar();
  updateStatusBarGit();
  if (getAuthMode() === "hosted") {
    loadFileTree(); // hosted: per-connection file tree (Phase 4)
    // Replay the resumed conversation's prior messages (Phase 4 conversation history).
    // Unawaited on purpose, so it races the run socket's resume replay (connectWs
    // below) — preserveLive makes the two compose in either completion order.
    const _cid = getActiveConnection();
    const _coid = getActiveConversation();
    if (_cid && _coid) {
      api.connectionMessages(_cid, _coid)
        .then((d) => { if (d && d.messages && d.messages.length) loadConversationMessages(d.messages, { preserveLive: true }); })
        .catch(() => {});
    }
  } else if (!didSwitch && state.tenant) {
    // No auto-switch happened — load file tree + sync ourselves
    loadFileTree();
  }
  // Connect the chat socket: local → /ws/chat; hosted → the per-connection run socket (ws.js bails
  // until a connection + conversation are bootstrapped in auth.js).
  connectWs();
  // Init session rail — fetch server session list and render the rail rows.
  initRail();

  // Delayed full remote compare (upgrades fast disk-only status to cloud compare).
  // Skipped if no tenant is connected yet or if switchTenant already triggered it.
  // Stored so initial_sync_done can cancel it (avoids redundant remote compare).
  if (state.tenant && !didSwitch) {
    state._delayedSyncTimer = setTimeout(() => {
      state._delayedSyncTimer = null;
      loadSyncStatuses(true);
    }, 2000);
  }

  // Unlock unless a tenant switch started initial sync (sync_done will unlock)
  if (!didSwitch) {
    unlockInput();
  }

  // Rehydrate the chat panel with any persisted conversation so a page
  // reload doesn't lose context. The server returns the same messages the
  // orchestrator has in memory; if it also has a saved Claude Code session_id
  // the next user message resumes that subprocess instead of starting fresh.
  // Local-only: hosted already rehydrated via connectionMessages above, and
  // /api/agent/conversation is a gated desktop route there (guaranteed 404).
  if (getAuthMode() !== "hosted") {
    try {
      const resp = await fetch("/api/agent/conversation?conversation_id=default");
      if (resp.ok) {
        const data = await resp.json();
        const msgs = (data.messages || []).filter(m => m && m.role && m.content);
        if (msgs.length > 0) {
          loadConversationMessages(msgs);
        }
      }
    } catch (e) {
      console.debug("Could not rehydrate persisted conversation:", e);
    }
  }

  // Refresh the backend picker label now that the active tenant is loaded.
  // wireBackendPicker() ran during _populateChatPanel before tenant init had
  // completed, so the label may show the env-var fallback instead of the
  // tenant's persisted coding_backend.
  if (typeof window.__refreshBackendPicker === "function") {
    window.__refreshBackendPicker().catch(() => { /* best effort */ });
  }

  // Save on page unload — conversations always autosave.
  window.addEventListener("beforeunload", () => {
    // Local-only: hosted transcripts persist server-side turn by turn, and the
    // beacon's /api/conversations target exists only in local/desktop mode.
    if (getAuthMode() === "hosted") return;
    const messages = getSerializableMessages();
    if (messages.length > 0 && state.activeConversationId === null) {
      // Use sendBeacon — only reliable method in beforeunload
      api.saveConversationBeacon(messages);
    }
  });
}

// Helpers used by E2E tests
window.loadSyncStatuses = loadSyncStatuses;
window.__selection = selection;
window.__appState = state;
// Lets E2E assert the pre-push confirm gate (a live-cloud push must be confirmed).
window.__pushArtifact = pushArtifact;
// Harmless test-affordance: lets E2E tests push a message through the page's
// own live WebSocket (e.g. a direct tool_call) so server-originated activity
// events render into the real chat shell instead of a hand-faked DOM node.
window.__cpqWsSend = wsSend;

// Global hook used by .md-flink anchors in assistant prose so we don't have
// to attach a click handler to every link individually after innerHTML writes.
window.__openChatFile = function (path) {
  // Normalize agent-emitted paths to canonical tenant-relative form. The
  // coding-agent workspace symlinks the active tenant as `tenant/`, so the
  // agent often refers to files as `tenant/global-scripts/X.py` — but the
  // /api/files endpoint resolves relative to the tenant directory, where
  // the canonical path is `global-scripts/X.py`. Also strip a literal
  // `<tenant-name>/` prefix in case the agent uses the real folder name.
  let normalized = path.replace(/^\.?\//, "");
  if (normalized.startsWith("tenant/")) {
    normalized = normalized.slice("tenant/".length);
  } else if (state.tenant && normalized.startsWith(state.tenant + "/")) {
    normalized = normalized.slice(state.tenant.length + 1);
  }
  // v1 shell: the Monaco workspace tab strip (search.js's openFileTab) -- unchanged.
  if (getShell() !== "v2") {
    const name = normalized.split("/").pop();
    const ext = name.split(".").pop().toLowerCase();
    const lang = ext === "py" ? "python" : ext === "html" ? "html" : ext === "json" ? "json" : "plaintext";
    openFileTab(normalized, name, lang);
    return;
  }

  // v2 shell: there is no workspace tab strip at all (init() returns before every
  // v1 panel/rail is wired), so openFileTab() would write into DOM that was never
  // built. Each path family the markdown renderer linkifies gets the nearest honest
  // destination instead:
  //   global-scripts/ | custom-calculations/ | quote-tables/
  //                    -> the Code view, opening the artifact in its own preview tab
  //                       (code-view.js's openArtifactInCode, which also accepts the
  //                       deep quote-tables/<T>/actions/<S>.py member grammar).
  //   ui-templates/    -> the Templates view. Navigation only: that view is a
  //                       badge-only sync list with no per-template detail pane to
  //                       open into, so there is nothing deeper to aim at today.
  //   knowledge/       -> the Knowledge view, same navigation-only reasoning.
  //   sap-help/        -> deliberately nothing. Those docs live outside the tenant
  //                       workspace and no v2 view renders them; a no-op beats
  //                       sending the user somewhere that cannot show the file.
  // Both imports are dynamic: shell-v2.js already imports THIS module, so a static
  // back-import would close a cycle, and neither module belongs in the v1 shell's
  // load path. Same precedent as assistant-drawer.js's continueInChat().
  if (normalized.startsWith("knowledge/") || normalized.startsWith("ui-templates/")) {
    const view = normalized.startsWith("knowledge/") ? "knowledge" : "templates";
    import("./shell-v2.js").then((m) => m.showViewV2(view)).catch(() => {});
    return;
  }
  import("./code-view.js").then((m) => m.openArtifactInCode(normalized)).catch(() => {});
};

// Delegated click handler for .md-flink anchors (file links in assistant prose). Replaces a
// per-link inline onclick so the markdown renderer emits no inline handlers (CSP-friendly) and
// links written via innerHTML at any time keep working.
document.addEventListener("click", (e) => {
  const a = e.target && e.target.closest ? e.target.closest("a.md-flink[data-path]") : null;
  if (!a) return;
  e.preventDefault();
  window.__openChatFile(a.getAttribute("data-path"));
});

// Boot through the auth gate: local/desktop mode runs init() directly; hosted mode requires a
// login session first (see auth.js).
window.addEventListener("DOMContentLoaded", () => boot(init));
